import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTimes, FaSyncAlt } from 'react-icons/fa';
import CollapsibleFilters from './CollapsibleFilters';

import JreatsOrderFilters from './JreatsOrderFilters';
import OrderHistoryDateFilters, {
    defaultOrderHistoryFromDate,
    defaultOrderHistoryToDate,
    orderMatchesHistoryDateRange,
} from './OrderHistoryDateFilters';
import OrderStatusFilterDropdown from './OrderStatusFilterDropdown';
import ToolbarRichSelect, { ORDER_VIEW_OPTIONS, ONLINE_CHANNEL_OPTIONS } from './ToolbarRichSelect';
import CateringQuotesPanel from './CateringQuotesPanel';
import CateringViewSwitch from './CateringViewSwitch';
import PartyHallPanel from './PartyHallPanel';
import SearchableSelect from './SearchableSelect';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import {
    buildSortHandlers,
    orderMatchesTableKeyword,
    sortOrderList,
} from './ordersTableUtils';
import { formatMobileDisplayOrFallback, formatOrderAltMobileDisplay, formatOrderMobileDisplay, formatPaymentMethodDisplay, formatOrderDateTime, formatDisplayOrderNo } from './formatDisplay';
import OrderStatusPill from './ui/OrderStatusPill';
import OrderBillingCell from './ui/OrderBillingCell';
import OrderItemsSummary from './ui/OrderItemsSummary';
import { getNextOrderStep, isTerminalStatus, resolveStatusKey } from './ui/orderStatusFlow';
import { confirmAction } from './ui/feedback';
import { canUpdateOrderStatus } from './rbacSession';
import './css/TodayOrder.css';
import './css/cateringViewSwitch.css';
import './css/OrderHistory.css';
import './css/Menu.css';
import './css/OrdersTablePagination.css';

import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
    fetchRestaurantOrderHistory,
    fetchRestaurantOrders,
    fetchPosOrdersWithPayments,
    isDineInMbOrder,
    cancelRestaurantOrder,
    cancelCateringOrder,
    updateRestaurantOrder,
    normalizeRestaurantOrder,
    resolveOrderId,
    resolvePartySize,
    formatTableNumberDisplay,
} from './orderApi';
import {
    extractRestaurantId,
    pickDefaultRestaurantFromList,
    resolveInitialRestaurant,
    fetchRestaurantMealDeliveryTimes,
} from './restaurantApi';
import {
    buildJreatsApiDeliveryTime,
    formatJreatsMealDeliveryDisplay,
} from './jreatsMealDeliveryTime';
import './css/Inventory.css';
function isMyEatsActiveOrderStatus(raw) {
    return !isTerminalStatus(raw);
}

/** Normalize order status for checkbox matching (hyphens/spaces removed, upper case). */
function normalizeOnlineOrderStatusForFilter(raw) {
    return String(raw ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
}

function orderCreatedDateRawFromRow(order) {
    if (!order || typeof order !== 'object') return '';
    const raw =
        order.createdDate ??
        order.createdAt ??
        order.CreatedDate ??
        order.created_date;
    return raw != null ? String(raw).trim() : '';
}

function parseOrderCreatedDate(order) {
    const s = orderCreatedDateRawFromRow(order);
    if (!s) return null;
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s) ? s.replace(' ', 'T') : s;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** True when order created_date falls on the current local calendar day. */
function isOrderCreatedToday(order) {
    const d = parseOrderCreatedDate(order);
    if (!d) return false;
    const now = new Date();
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
}

function isJreatsDeliveredOrCancelledStatus(order) {
    const norm = normalizeOnlineOrderStatusForFilter(
        order.orderStatus ?? order.OrderStatus ?? order.order_status ?? ''
    );
    return norm === 'DELIVERED' || norm === 'CANCELLED';
}

function isCancelledOrderStatus(order) {
    return (
        normalizeOnlineOrderStatusForFilter(
            order?.orderStatus ?? order?.OrderStatus ?? order?.order_status ?? ''
        ) === 'CANCELLED'
    );
}

/** Jr Eats: today's delivered/cancelled belong in Active Orders, not Order History. */
function isJreatsTodayTerminalOrder(order) {
    return isOrderCreatedToday(order) && isJreatsDeliveredOrCancelledStatus(order);
}

/** True when order was created before the current local calendar day. */
function isOrderCreatedBeforeToday(order) {
    const d = parseOrderCreatedDate(order);
    if (!d) return false;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const orderDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return orderDay < todayStart;
}

const UNDELIVERED_EXCLUDED_STATUS_NORMS = [
    'DELIVERED',
    'DEL',
    'CANCELLED',
    'CAN',
    'COMPLETED',
    'COMPLETE',
    'CMP',
    'PAID',
];

function isUndeliveredPastOrderStatus(order) {
    const norm = normalizeOnlineOrderStatusForFilter(
        order.orderStatus ?? order.OrderStatus ?? order.order_status ?? ''
    );
    return !UNDELIVERED_EXCLUDED_STATUS_NORMS.includes(norm);
}

/** Order history — prior dates, not delivered / cancelled / completed. */
function orderMatchesUndeliveredHistoryFilter(order) {
    return isOrderCreatedBeforeToday(order) && isUndeliveredPastOrderStatus(order);
}

const ORDER_HISTORY_UNDELIVERED_OPT = {
    code: 'HIST_UNDL',
    label: 'UNDELIVERED',
    matchNorm: [],
};

const JREATS_HISTORY_UNDELIVERED_OPT = {
    code: 'UND',
    label: 'UNDELIVERED',
    matchNorm: [],
};

function isOrderHistoryUndeliveredOpt(opt) {
    return opt?.code === 'HIST_UNDL' || opt?.code === 'UND';
}

/** Online Orders tab — status checkbox filter (display labels hyphenated where applicable; matching unchanged vs API normalization). */
const ONLINE_ORDER_STATUS_CHECKBOX_OPTIONS = [
    { code: 'NEW', label: 'NEW', matchNorm: ['NEW'] },
    { code: 'ACC', label: 'ACCEPTED', matchNorm: ['ACCEPTED', 'ACC'] },
    { code: 'PREP', label: 'PREPARING', matchNorm: ['PREPARING', 'PREP'] },
    { code: 'RDY', label: 'READY', matchNorm: ['READY', 'RDY'] },
    { code: 'OFD', label: 'OUT-FOR-DELIVERY', matchNorm: ['OUTFORDELIVERY', 'OFD'] },
    { code: 'PU', label: 'PICKED-UP', matchNorm: ['PICKEDUP', 'PU'] },
    { code: 'SRV', label: 'SERVED', matchNorm: ['SERVED', 'SRV'] },
];

/** Default Orders tab filter: none checked — show all orders until user picks statuses. */
function buildEmptyStatusSelections(opts) {
    return Object.fromEntries(opts.map((o) => [o.code, false]));
}

function buildOnlineOrderStatusSelectionsDefault() {
    return buildEmptyStatusSelections(ONLINE_ORDER_STATUS_CHECKBOX_OPTIONS);
}

/** MyEats / JREats online order tabs (replaces legacy single "online" + source dropdown). */
function isOnlineOrdersView(orderTypeFilter) {
    return orderTypeFilter === 'myeats' || orderTypeFilter === 'jreats' || orderTypeFilter === 'online';
}

function getOnlineSourceFilter(orderTypeFilter) {
    if (orderTypeFilter === 'myeats') return 'MYEATS';
    if (orderTypeFilter === 'jreats') return 'JREATS';
    return 'MYEATS';
}

function showOrganizationColumnForOrders(orderTypeFilter) {
    return orderTypeFilter !== 'myeats';
}

function isMyEatsOrdersView(orderTypeFilter) {
    return orderTypeFilter === 'myeats' || orderTypeFilter === 'online';
}

function isPickupOnlineOrder(order) {
    const ordType = String(
        order?.orderType ?? order?.Order_Type ?? order?.order_type ?? 'DELIVERY'
    )
        .trim()
        .toUpperCase();
    return ordType === 'PICKUP';
}

function showMyEatsDeliveryAgentColumn(orderTypeFilter, onlineOrderTypeFilter) {
    if (!isMyEatsOrdersView(orderTypeFilter)) return true;
    return onlineOrderTypeFilter !== 'PICKUP';
}

function onlineOrdersTableColSpan(onlineOrderType, orderTypeFilter, onlineOrderTypeFilter = 'DELIVERY') {
    if (orderTypeFilter === 'jreats') {
        return onlineOrderType === 'orderHistory' ? 13 : 14;
    }
    let span = onlineOrderType === 'orderHistory' ? 10 : 11;
    if (isMyEatsOrdersView(orderTypeFilter)) {
        span -= 1;
        if (onlineOrderTypeFilter === 'PICKUP') span -= 1;
    }
    return span;
}

function formatJreatsMealType(order) {
    const meal =
        order.mealType ??
        order.meal_type ??
        order.Meal_Type ??
        order.meal ??
        order.Meal;
    if (meal != null && String(meal).trim() !== '') return String(meal).trim();
    return 'N/A';
}

/** Junior Eats Order History — status filter dropdown. */
const JREATS_ORDER_HISTORY_STATUS_OPTS = [
    { code: 'DEL', label: 'DELIVERED', matchNorm: ['DELIVERED', 'DEL'] },
    { code: 'CAN', label: 'CANCELLED', matchNorm: ['CANCELLED', 'CAN'] },
    JREATS_HISTORY_UNDELIVERED_OPT,
];

/** Junior Eats Active Orders — same statuses as history plus NEW and OUT FOR DELIVERY. */
const JREATS_ORDER_STATUS_OPTS = [
    { code: 'NEW', label: 'NEW', matchNorm: ['NEW'] },
    {
        code: 'OFD',
        label: 'OUT FOR DELIVERY',
        matchNorm: ['OUTFORDELIVERY', 'OFD', 'INTRANSIT', 'IN-TRANSIT'],
    },
    ...JREATS_ORDER_HISTORY_STATUS_OPTS,
];

/** Junior Eats — update-order status dropdown only. */
const JREATS_STATUS_UPDATE_OPTIONS = [
    'NEW',
    'OUT-FOR-DELIVERY',
    'DELIVERED',
    'CANCELLED',
];

/** My Eats — no separate Accepted step; kitchen moves NEW straight to cooking. */
const MYEATS_STATUS_UPDATE_OPTIONS = [
    'NEW',
    'PREPARING',
    'READY',
    'OUT-FOR-DELIVERY',
    'PICKED-UP',
    'DELIVERED',
    'CANCELLED',
];

function onlineStatusForSelect(raw) {
    const key = resolveStatusKey(raw);
    const map = {
        NEW: 'NEW',
        ACCEPTED: 'ACCEPTED',
        PREPARING: 'PREPARING',
        READY: 'READY',
        OFD: 'OUT-FOR-DELIVERY',
        PICKEDUP: 'PICKED-UP',
        DELIVERED: 'DELIVERED',
        CANCELLED: 'CANCELLED',
    };
    return map[key] || 'NEW';
}

function jreatsStatusForSelect(raw) {
    const norm = normalizeOnlineOrderStatusForFilter(raw);
    const map = {
        NEW: 'NEW',
        OUTFORDELIVERY: 'OUT-FOR-DELIVERY',
        OFD: 'OUT-FOR-DELIVERY',
        INTRANSIT: 'OUT-FOR-DELIVERY',
        DELIVERED: 'DELIVERED',
        DEL: 'DELIVERED',
        CANCELLED: 'CANCELLED',
        CAN: 'CANCELLED',
    };
    if (map[norm]) return map[norm];
    return 'NEW';
}

function jreatsStatusOptionLabel(status) {
    return String(status ?? '').replace(/-/g, ' ');
}

function buildJreatsOrderStatusSelectionsDefault() {
    return Object.fromEntries(
        JREATS_ORDER_STATUS_OPTS.map((o) => [o.code, o.code === 'NEW'])
    );
}

function buildJreatsHistoryStatusSelectionsDefault() {
    return buildEmptyStatusSelections(JREATS_ORDER_HISTORY_STATUS_OPTS);
}

function jreatsOrderPassesStatusFilter(order, selections, opts) {
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const selected = opts.filter((o) => selections[o.code]);
    if (selected.length === 0) return true;
    const norm = normalizeOnlineOrderStatusForFilter(order.orderStatus ?? order.OrderStatus);
    for (const opt of selected) {
        if (isOrderHistoryUndeliveredOpt(opt)) {
            if (orderMatchesUndeliveredHistoryFilter(order)) return true;
            continue;
        }
        if (opt.matchNorm.includes(norm)) return true;
    }
    return false;
}

function orderMatchesJreatsMealFilter(order, mealFilter) {
    if (!mealFilter) return true;
    const meal = formatJreatsMealType(order);
    if (meal === 'N/A') return false;
    return meal.toLowerCase() === String(mealFilter).toLowerCase();
}

/** Dine-in active orders — status checkbox filters. */
const DINE_IN_ACTIVE_STATUS_OPTS = [
    { code: 'NEW', label: 'NEW', matchNorm: ['NEW'] },
    { code: 'PREP', label: 'PREPARING', matchNorm: ['PREPARING', 'PREP'] },
    { code: 'RDY', label: 'READY', matchNorm: ['READY', 'RDY'] },
    { code: 'SRV', label: 'SERVED', matchNorm: ['SERVED', 'SRV', 'SERVING'] },
];

/** Dine-in order history — terminal statuses only. */
const DINE_IN_HISTORY_STATUS_OPTS = [
    { code: 'HIST_CMP', label: 'COMPLETED', matchNorm: ['COMPLETED', 'COMPLETE', 'DELIVERED', 'PAID'] },
    { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED', 'CAN'] },
];

const DINE_IN_STATUS_UPDATE_OPTIONS = ['NEW', 'PREPARING', 'READY', 'SERVED'];

const DINE_IN_HISTORY_STATUS_NORMS = ['COMPLETED', 'COMPLETE', 'DELIVERED', 'PAID', 'CANCELLED', 'CAN'];

function normalizeOfflineOrderView(val) {
    if (val === 'delivered' || val === 'orderHistory') return 'orderHistory';
    return 'orders';
}

function isDineInOfflineHistoryView(offlineOrderType) {
    return offlineOrderType === 'orderHistory' || offlineOrderType === 'delivered';
}

function isDineInOfflineOrdersView(offlineOrderType) {
    return offlineOrderType === 'orders' || offlineOrderType === 'save';
}

function isDineInOrderType(order) {
    return (
        String(order.orderType ?? order.order_type ?? order.Order_Type ?? '')
            .trim()
            .toUpperCase() === 'DINE-IN'
    );
}

function orderHasTableNumber(order) {
    const tableNum =
        order.tableNumber ??
        order.table_number ??
        order.TableNumber ??
        order.table_no ??
        order.tableNo ??
        order.Table_No;
    return (
        tableNum !== null &&
        tableNum !== undefined &&
        tableNum !== '' &&
        String(tableNum).trim() !== '' &&
        tableNum !== 0 &&
        tableNum !== '0'
    );
}

function isDineInPosOrder(order) {
    return orderHasTableNumber(order) || isDineInOrderType(order);
}

function buildDineInActiveStatusSelectionsDefault() {
    return buildEmptyStatusSelections(DINE_IN_ACTIVE_STATUS_OPTS);
}

function normalizeDineInOrderStatusNorm(order) {
    return normalizeOnlineOrderStatusForFilter(
        order.orderStatus ?? order.OrderStatus ?? order.order_status ?? 'NEW'
    );
}

function isDineInHistoryOrderStatus(norm) {
    return DINE_IN_HISTORY_STATUS_NORMS.includes(norm);
}

function dineInOrderPassesActiveStatusFilter(order, selections) {
    const opts = DINE_IN_ACTIVE_STATUS_OPTS;
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = opts.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeDineInOrderStatusNorm(order);
    for (const opt of opts) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

function dineInOrderPassesHistoryStatusFilter(order, selections) {
    const opts = DINE_IN_HISTORY_STATUS_OPTS;
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = opts.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeDineInOrderStatusNorm(order);
    for (const opt of opts) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

/** Map dine-in display status to API order_status_code. */
function dineInDisplayStatusToOrderStatusCode(raw) {
    const norm = normalizeOnlineOrderStatusForFilter(raw);
    const map = {
        NEW: 'NEW',
        PREPARING: 'PREP',
        PREP: 'PREP',
        READY: 'RDY',
        RDY: 'RDY',
        SERVING: 'SRV',
        SERVED: 'SRV',
        SRV: 'SRV',
        COMPLETED: 'CMP',
        COMPLETE: 'CMP',
        DELIVERED: 'DEL',
        CANCELLED: 'CAN',
        CAN: 'CAN',
    };
    return map[norm] || 'NEW';
}

/** Normalize stored/API status for dine-in status dropdown display. */
function dineInStatusForSelect(raw) {
    const norm = normalizeOnlineOrderStatusForFilter(raw);
    if (norm === 'SRV' || norm === 'SERVED' || norm === 'SERVING') return 'SERVED';
    if (norm === 'PREP') return 'PREPARING';
    if (norm === 'RDY') return 'READY';
    if (DINE_IN_STATUS_UPDATE_OPTIONS.includes(norm)) return norm;
    return 'NEW';
}

/** Catering active orders — status checkbox filters. */
const CATERING_ACTIVE_STATUS_OPTS = [
    { code: 'NEW', label: 'NEW', matchNorm: ['NEW', 'PENDING'] },
    { code: 'PREP', label: 'PREPARING', matchNorm: ['PREPARING', 'PREP', 'INPROGRESS', 'IN-PROGRESS'] },
    { code: 'RDY', label: 'READY', matchNorm: ['READY', 'RDY'] },
    { code: 'OFD', label: 'OUT-FOR-DELIVERY', matchNorm: ['OUTFORDELIVERY', 'OFD', 'INTRANSIT', 'IN-TRANSIT', 'IN_TRANSIT'] },
];

/** Catering order history — terminal statuses. */
const CATERING_HISTORY_STATUS_OPTS = [
    { code: 'HIST_DLVD', label: 'DELIVERED', matchNorm: ['DELIVERED', 'DEL'] },
    { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED', 'CAN'] },
    { code: 'HIST_CMP', label: 'COMPLETED', matchNorm: ['COMPLETED', 'COMPLETE', 'CMP'] },
];

const CATERING_STATUS_UPDATE_OPTIONS = ['NEW', 'PREPARING', 'READY', 'OUT-FOR-DELIVERY'];

const CATERING_HISTORY_STATUS_NORMS = ['DELIVERED', 'DEL', 'CANCELLED', 'CAN', 'COMPLETED', 'COMPLETE', 'CMP'];

/** Canonical status key (orderStatusFlow) -> active-filter checkbox code, per channel. */
const ONLINE_CANON_TO_CODE = {
    NEW: 'NEW',
    ACCEPTED: 'ACC',
    PREPARING: 'PREP',
    READY: 'RDY',
    OFD: 'OFD',
    PICKEDUP: 'PU',
    SERVED: 'SRV',
};
const JREATS_CANON_TO_CODE = { NEW: 'NEW', OFD: 'OFD' };
const CATERING_CANON_TO_CODE = { NEW: 'NEW', PREPARING: 'PREP', READY: 'RDY', OFD: 'OFD' };
const DINE_IN_CANON_TO_CODE = { NEW: 'NEW', PREPARING: 'PREP', READY: 'RDY', SERVED: 'SRV' };

/** Build a status-selection object where only `code` is checked. */
function buildSingleStatusSelection(opts, code) {
    return Object.fromEntries(opts.map((o) => [o.code, o.code === code]));
}

function normalizeCateringOrderView(val) {
    if (val === 'quotes') return 'quotes';
    if (val === 'completed' || val === 'orderHistory') return 'orderHistory';
    return 'orders';
}

function isCateringQuotesView(cateringOrderType) {
    return cateringOrderType === 'quotes';
}

function isCateringHistoryView(cateringOrderType) {
    return cateringOrderType === 'orderHistory' || cateringOrderType === 'completed';
}

function buildCateringActiveStatusSelectionsDefault() {
    return buildEmptyStatusSelections(CATERING_ACTIVE_STATUS_OPTS);
}

function normalizeCateringOrderStatusNorm(order) {
    const raw =
        order.orderStatusCode ??
        order.order_status_code ??
        order.Order_Status_Code ??
        order.orderStatus ??
        order.OrderStatus ??
        order.order_status ??
        'NEW';
    return normalizeOnlineOrderStatusForFilter(raw);
}

function isCateringHistoryOrderStatus(norm) {
    return CATERING_HISTORY_STATUS_NORMS.includes(norm);
}

function cateringOrderPassesActiveStatusFilter(order, selections) {
    const opts = CATERING_ACTIVE_STATUS_OPTS;
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = opts.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeCateringOrderStatusNorm(order);
    for (const opt of opts) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

function cateringOrderPassesHistoryStatusFilter(order, selections) {
    const opts = CATERING_HISTORY_STATUS_OPTS;
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = opts.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeCateringOrderStatusNorm(order);
    for (const opt of opts) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

/** Normalize stored/API status for catering status dropdown display. */
function cateringStatusForSelect(raw) {
    const norm = normalizeOnlineOrderStatusForFilter(raw);
    if (norm === 'PREP' || norm === 'INPROGRESS') return 'PREPARING';
    if (norm === 'RDY') return 'READY';
    if (norm === 'OFD' || norm === 'OUTFORDELIVERY' || norm === 'INTRANSIT') return 'OUT-FOR-DELIVERY';
    if (norm === 'PENDING') return 'NEW';
    if (CATERING_STATUS_UPDATE_OPTIONS.includes(norm)) return norm;
    return 'NEW';
}

function cateringDisplayStatusToOrderStatusCode(raw) {
    const norm = normalizeOnlineOrderStatusForFilter(raw);
    const map = {
        NEW: 'NEW',
        PENDING: 'NEW',
        PREPARING: 'PREP',
        PREP: 'PREP',
        INPROGRESS: 'PREP',
        READY: 'RDY',
        RDY: 'RDY',
        OUTFORDELIVERY: 'OFD',
        OFD: 'OFD',
        INTRANSIT: 'OFD',
        DELIVERED: 'DEL',
        DEL: 'DEL',
        COMPLETED: 'CMP',
        COMPLETE: 'CMP',
        CMP: 'CMP',
        CANCELLED: 'CAN',
        CAN: 'CAN',
    };
    return map[norm] || 'NEW';
}

function cateringDisplayStatusLabel(order) {
    const norm = normalizeCateringOrderStatusNorm(order);
    if (norm === 'OFD' || norm === 'OUTFORDELIVERY' || norm === 'INTRANSIT') return 'OUT-FOR-DELIVERY';
    if (norm === 'PREP' || norm === 'INPROGRESS') return 'PREPARING';
    if (norm === 'RDY') return 'READY';
    if (norm === 'PENDING') return 'NEW';
    if (norm === 'DEL') return 'DELIVERED';
    if (norm === 'CMP' || norm === 'COMPLETE') return 'COMPLETED';
    if (norm === 'CAN') return 'CANCELLED';
    return order.orderStatus || order.OrderStatus || 'NEW';
}

/** Child school fields for Junior Eats orders (from API + children_t lookup). */
function getJreatsChildSchoolDisplay(order) {
    const val = (v) => (v != null && String(v).trim() !== '' ? String(v) : 'N/A');
    const cls = order.childClass ?? order.child_class ?? order.class;
    const section = order.section ?? order.Section;
    return {
        rollNumber: val(order.rollNumber ?? order.roll_number),
        className: cls != null && String(cls).trim() !== '' ? String(cls).trim() : 'N/A',
        section: section != null && String(section).trim() !== '' ? String(section).trim() : 'N/A',
    };
}

/** PICKED-UP / SERVED omitted for Junior Eats, DELIVERY, and PICKUP; OUT-FOR-DELIVERY omitted for PICKUP only. */
function getOnlineOrderStatusCheckboxOptionsForType(onlineOrderTypeFilter, orderTypeFilter) {
    let opts = ONLINE_ORDER_STATUS_CHECKBOX_OPTIONS;
    if (
        orderTypeFilter === 'jreats' ||
        onlineOrderTypeFilter === 'DELIVERY' ||
        onlineOrderTypeFilter === 'PICKUP'
    ) {
        opts = opts.filter((o) => o.code !== 'PU' && o.code !== 'SRV');
    }
    if (onlineOrderTypeFilter === 'PICKUP') {
        opts = opts.filter((o) => o.code !== 'OFD');
    }
    if (orderTypeFilter === 'myeats') {
        opts = opts.filter((o) => o.code !== 'ACC');
    }
    return opts;
}

const ONLINE_STATUS_UPDATE_OPTIONS = [
    'NEW',
    'ACCEPTED',
    'PREPARING',
    'READY',
    'OUT-FOR-DELIVERY',
    'PICKED-UP',
    'SERVED',
    'DELIVERED',
    'CANCELLED',
];

function getOnlineStatusUpdateOptions(orderTypeFilter, onlineOrderTypeFilter = 'DELIVERY') {
    if (orderTypeFilter === 'jreats') {
        return JREATS_STATUS_UPDATE_OPTIONS;
    }
    if (isMyEatsOrdersView(orderTypeFilter)) {
        if (onlineOrderTypeFilter === 'PICKUP') {
            return MYEATS_STATUS_UPDATE_OPTIONS.filter((s) => s !== 'OUT-FOR-DELIVERY');
        }
        return MYEATS_STATUS_UPDATE_OPTIONS;
    }
    return ONLINE_STATUS_UPDATE_OPTIONS;
}

function getMyEatsStatusUpdateOptionsForOrder(onlineOrderTypeFilter, order) {
    if (onlineOrderTypeFilter === 'PICKUP' || isPickupOnlineOrder(order)) {
        return MYEATS_STATUS_UPDATE_OPTIONS.filter((s) => s !== 'OUT-FOR-DELIVERY');
    }
    return MYEATS_STATUS_UPDATE_OPTIONS;
}

function onlineOrderPassesStatusCheckboxFilter(order, selections, onlineOrderTypeFilter, orderTypeFilter) {
    const opts = getOnlineOrderStatusCheckboxOptionsForType(onlineOrderTypeFilter, orderTypeFilter);
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = opts.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeOnlineOrderStatusForFilter(order.orderStatus ?? order.OrderStatus);
    for (const opt of opts) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

/** Order History tab — status filters depend on DELIVERY vs PICKUP (vs all). */
const ORDER_HISTORY_DELIVERY_OPTS = [
    { code: 'HIST_DLVD', label: 'DELIVERED', matchNorm: ['DELIVERED'] },
    { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED'] },
    { code: 'HIST_CMP', label: 'COMPLETED', matchNorm: ['COMPLETED'] },
    ORDER_HISTORY_UNDELIVERED_OPT,
];
const ORDER_HISTORY_PICKUP_OPTS = [
    { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED'] },
    { code: 'HIST_PU', label: 'PICKED-UP', matchNorm: ['PICKEDUP', 'PU'] },
    { code: 'HIST_CMP', label: 'COMPLETED', matchNorm: ['COMPLETED'] },
    ORDER_HISTORY_UNDELIVERED_OPT,
];
const ORDER_HISTORY_ALL_OPTS = [
    { code: 'HIST_DLVD', label: 'DELIVERED', matchNorm: ['DELIVERED'] },
    { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED'] },
    { code: 'HIST_PU', label: 'PICKED-UP', matchNorm: ['PICKEDUP', 'PU'] },
    { code: 'HIST_CMP', label: 'COMPLETED', matchNorm: ['COMPLETED'] },
    ORDER_HISTORY_UNDELIVERED_OPT,
];

function getOrderHistoryStatusCheckboxOptions(onlineOrderTypeFilter, orderTypeFilter) {
    if (onlineOrderTypeFilter === 'DELIVERY') return ORDER_HISTORY_DELIVERY_OPTS;
    if (onlineOrderTypeFilter === 'PICKUP') return ORDER_HISTORY_PICKUP_OPTS;
    if (orderTypeFilter === 'jreats') {
        return ORDER_HISTORY_ALL_OPTS.filter((o) => o.code !== 'HIST_PU');
    }
    return ORDER_HISTORY_ALL_OPTS;
}

function allowedOrderHistoryStatusesForType(onlineOrderTypeFilter, orderTypeFilter) {
    const undelivered = ['UNDELIVERED', 'UND'];
    if (onlineOrderTypeFilter === 'DELIVERY') {
        return ['DELIVERED', 'CANCELLED', 'COMPLETED', ...undelivered];
    }
    if (onlineOrderTypeFilter === 'PICKUP') {
        return ['CANCELLED', 'PICKEDUP', 'PU', 'COMPLETED', ...undelivered];
    }
    if (orderTypeFilter === 'jreats') return ['DELIVERED', 'CANCELLED', ...undelivered];
    return ['DELIVERED', 'CANCELLED', 'PICKEDUP', 'PU', 'COMPLETED', ...undelivered];
}

function onlineOrderHistoryPassesStatusCheckboxFilter(order, selections, onlineOrderTypeFilter, orderTypeFilter) {
    const opts = getOrderHistoryStatusCheckboxOptions(onlineOrderTypeFilter, orderTypeFilter);
    const allSel = opts.every((o) => selections[o.code]);
    if (allSel) return true;
    const selected = opts.filter((o) => selections[o.code]);
    if (selected.length === 0) return true;
    const norm = normalizeOnlineOrderStatusForFilter(order.orderStatus ?? order.OrderStatus);
    for (const opt of selected) {
        if (isOrderHistoryUndeliveredOpt(opt)) {
            if (orderMatchesUndeliveredHistoryFilter(order)) return true;
            continue;
        }
        if (opt.matchNorm.includes(norm)) return true;
    }
    return false;
}

/** Map displayed / stored order status to backend update_order.order_status_code. */
function onlineDisplayStatusToOrderStatusCode(raw) {
    const norm = normalizeOnlineOrderStatusForFilter(raw);
    const map = {
        NEW: 'NEW',
        ACC: 'ACC',
        ACCEPTED: 'ACC',
        PREPARING: 'PREP',
        PREP: 'PREP',
        READY: 'RDY',
        RDY: 'RDY',
        OUTFORDELIVERY: 'OFD',
        OFD: 'OFD',
        PICKEDUP: 'PU',
        PU: 'PU',
        SERVED: 'SRV',
        SRV: 'SRV',
        DELIVERED: 'DEL',
        CANCELLED: 'CAN',
        INTRANSIT: 'OFD',
        INTRANST: 'OFD',
    };
    return map[norm] || 'ACC';
}

/** API expects local datetime like "2026-05-01 17:30:52". Returns null if no usable value. */
function formatDeliveryTimeForUpdateApi(raw) {
    if (raw == null || raw === '') return null;
    const s = String(raw).trim();
    if (!s || s.toUpperCase() === 'N/A') return null;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    const padLocal = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${padLocal(d.getMonth() + 1)}-${padLocal(d.getDate())} ${padLocal(d.getHours())}:${padLocal(d.getMinutes())}:${padLocal(d.getSeconds())}`;
}

function deliveryTimeNowForUpdateApi() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function deliveryTimeRawFromOrder(order) {
    if (!order || typeof order !== 'object') return null;
    return (
        order.deliveryTime ??
        order.DeliveryTime ??
        order.Delivery_Time ??
        order.scheduledDeliveryTime ??
        order.ScheduledDeliveryTime ??
        null
    );
}

function resolveOnlineDeliveryTimeForUpdate(
    orderRow,
    deliveryTimeModalValue,
    orderTypeFilter,
    jreatsMealTimes
) {
    if (orderTypeFilter === 'jreats' && orderRow) {
        return buildJreatsApiDeliveryTime(orderRow, jreatsMealTimes) ?? deliveryTimeNowForUpdateApi();
    }
    if (deliveryTimeModalValue != null && String(deliveryTimeModalValue).trim() !== '') {
        return formatDeliveryTimeForUpdateApi(deliveryTimeModalValue) ?? deliveryTimeNowForUpdateApi();
    }
    return (
        formatDeliveryTimeForUpdateApi(deliveryTimeRawFromOrder(orderRow)) ??
        deliveryTimeNowForUpdateApi()
    );
}

/** Value for input[type=datetime-local] (minute precision) from API "YYYY-MM-DD HH:mm:ss". */
function toDatetimeLocalInputValue(apiFormatted) {
    if (!apiFormatted) return '';
    const s = String(apiFormatted).trim();
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) return s.slice(0, 16).replace(' ', 'T');
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Delivery-agent roster rows may expose id under several keys — must match <option value> ↔ state. */
function getDeliveryAgentRecordId(agent) {
    if (!agent || typeof agent !== 'object') return '';
    const id =
        agent.userId ??
        agent.User_Id ??
        agent.userID ??
        agent.user_id ??
        agent.id ??
        agent.Id ??
        agent.UserId;
    if (id === null || id === undefined || id === '') return '';
    return String(id);
}

function getDeliveryAgentDisplayName(agent) {
    if (!agent || typeof agent !== 'object') return 'User';
    const n =
        agent.user_name ??
        agent.userName ??
        agent.UserName ??
        agent.User_Name ??
        `${agent.firstName || agent.first_name || ''} ${agent.lastName || agent.last_name || ''}`.trim();
    return (typeof n === 'string' && n.trim() !== '') ? n.trim() : 'User';
}

function getDeliveryAgentPhone(agent) {
    if (!agent || typeof agent !== 'object') return '';
    const p = agent.mobile ?? agent.Mobile ?? agent.contactNumber ?? agent.phone ?? agent.Phone ?? '';
    return String(p ?? '').trim();
}

function deliveryContactIdFromOrderRow(orderRow) {
    if (!orderRow || typeof orderRow !== 'object') return '';
    const id =
        orderRow.deliveryContactId ??
        orderRow.delivery_contact_id ??
        orderRow.Delivery_Contact_Id ??
        orderRow.DeliveryContactId ??
        orderRow.Delivery_User_Id ??
        orderRow.delivery_user_id ??
        orderRow.User_Id ??
        orderRow.userId ??
        null;
    if (id === null || id === undefined || id === '') return '';
    return String(id);
}

function deliveryContactNameFromOrderRow(orderRow) {
    if (!orderRow || typeof orderRow !== 'object') return '';
    const n =
        orderRow.deliveryContactName ??
        orderRow.delivery_contact_name ??
        orderRow.Delivery_Contact_Name ??
        '';
    const s = String(n ?? '').trim();
    if (!s || s.toUpperCase() === 'N/A') return '';
    return s;
}

/** Persist API PascalCase/snake_case onto canonical props for grid + modal. */
function withCanonicalOnlineDeliveryFields(order) {
    if (!order || typeof order !== 'object') return order;
    const name = deliveryContactNameFromOrderRow(order);
    const idStr = deliveryContactIdFromOrderRow(order);
    const phone = deliveryContactPhoneFromOrderRow(order);
    const next = { ...order };
    const orderId = resolveOrderId(order);
    if (orderId != null) {
        next.orderId = orderId;
        next.order_id = orderId;
        next.OrderId = orderId;
    }
    if (name) next.deliveryContactName = name;
    if (phone) next.deliveryContactNo = phone;
    if (idStr) {
        const n = Number(idStr);
        if (Number.isFinite(n)) {
            next.deliveryContactId = n;
            next.delivery_contact_id = n;
        }
    }
    return next;
}

function deliveryContactPhoneFromOrderRow(orderRow) {
    if (!orderRow || typeof orderRow !== 'object') return '';
    const p =
        orderRow.deliveryContactNo ??
        orderRow.deliveryContactNumber ??
        orderRow.Delivery_Contact_No ??
        orderRow.Delivery_Contact_Number ??
        orderRow.delivery_contact_phone ??
        orderRow.delivery_contact_phone_number ??
        orderRow.Contact_Number ??
        '';
    const s = String(p ?? '').replace(/\s/g, '').trim();
    return s && s !== 'N/A' ? s : '';
}

function normalizeAgentNameMatchKey(name) {
    return String(name ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
}

function phoneDigitsOnly(phone) {
    return String(phone ?? '').replace(/\D/g, '');
}

/**
 * Prefer id from order ↔ agent list; else match by contact name / phone (get_orders exposes Delivery_Contact_Name).
 */
function resolveAssignedAgentRecordId(orderRow, agentsList) {
    if (!Array.isArray(agentsList) || agentsList.length === 0) return '';

    const idStr = deliveryContactIdFromOrderRow(orderRow);
    if (idStr !== '') {
        const byExact = agentsList.find((a) => getDeliveryAgentRecordId(a) === idStr);
        if (byExact) return getDeliveryAgentRecordId(byExact);

        const idNum = Number(idStr.replace(/\D/g, ''));
        if (!Number.isNaN(idNum) && idNum !== 0) {
            const byNum = agentsList.find((a) => Number(getDeliveryAgentRecordId(a)) === idNum);
            if (byNum) return getDeliveryAgentRecordId(byNum);
        }
    }

    const nameFromOrder = deliveryContactNameFromOrderRow(orderRow);
    const nameKey = normalizeAgentNameMatchKey(nameFromOrder);
    if (nameKey) {
        const byNameExact = agentsList.filter(
            (a) =>
                normalizeAgentNameMatchKey(getDeliveryAgentDisplayName(a)) === nameKey ||
                normalizeAgentNameMatchKey(
                    `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim()
                ) === nameKey
        );
        if (byNameExact.length === 1) return getDeliveryAgentRecordId(byNameExact[0]);
        /** Single partial match — e.g. order "Rakesh" vs roster "Rakesh Kumar". */
        if (byNameExact.length === 0) {
            const starts = agentsList.filter((a) => {
                const ad = normalizeAgentNameMatchKey(getDeliveryAgentDisplayName(a));
                return ad.startsWith(nameKey) || nameKey.startsWith(ad);
            });
            if (starts.length === 1) return getDeliveryAgentRecordId(starts[0]);
        }
    }

    const orderDigits = phoneDigitsOnly(deliveryContactPhoneFromOrderRow(orderRow));
    if (orderDigits.length >= 7) {
        const byPhone = agentsList.filter((a) => {
            const ad = phoneDigitsOnly(getDeliveryAgentPhone(a));
            return ad !== '' && (ad === orderDigits || ad.endsWith(orderDigits) || orderDigits.endsWith(ad));
        });
        if (byPhone.length === 1) return getDeliveryAgentRecordId(byPhone[0]);
    }

    return '';
}

const TodayOrder = ({ userId, orderTypeFilter = 'offline', offlineOrderType: propOfflineOrderType = 'orders', cateringOrderType: propCateringOrderType = 'orders', partyHallOrderType: propPartyHallOrderType = 'quotes', onlineOrderType, setOnlineOrderType, initialActiveStatus = '', serviceChannelSelect = null }) => {
    const allowOrderStatusUpdate = canUpdateOrderStatus();
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(() => localStorage.getItem('restaurantId') || '');
    const [orders, setOrders] = useState([]);
    const [selectedOrderItems, setSelectedOrderItems] = useState(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    const [selectedStatusForUpdate, setSelectedStatusForUpdate] = useState('NEW');
    const [deliveryAgents, setDeliveryAgents] = useState([]);
    /** Selected user in delivery-agent dropdowns (userId as string; empty = none). */
    const [selectedDeliveryAgentId, setSelectedDeliveryAgentId] = useState('');
    const [deliveryAgentSearch, setDeliveryAgentSearch] = useState('');
    const deliveryAgentDropdownRef = useRef(null);
    /** Update-order modal — datetime-local control (online). */
    const [deliveryTimeModalLocal, setDeliveryTimeModalLocal] = useState('');
    const [jreatsMealDeliveryTimes, setJreatsMealDeliveryTimes] = useState(null);
    /** While POST/PUT assigning delivery agent; avoids duplicate submits. */
    const [deliveryAssignSaving, setDeliveryAssignSaving] = useState(false);
    const [assignedAgents, setAssignedAgents] = useState({}); 
    const [tables, setTables] = useState([]); 
    const [currentPageNew, setCurrentPageNew] = useState(1); // Pagination for Save Orders
    const [currentPageDelivered, setCurrentPageDelivered] = useState(1); // Pagination for Delivered Orders
    const [currentPageCatering, setCurrentPageCatering] = useState(1); // Pagination for Catering Orders
    const [currentPageOnline, setCurrentPageOnline] = useState(1); // Pagination for Online Orders
    const [ordersPageSize, setOrdersPageSize] = useState(10);
    const [searchOrderIdNew, setSearchOrderIdNew] = useState(''); // Search for Save Orders
    const [searchOrderIdDelivered, setSearchOrderIdDelivered] = useState(''); // Search for Delivered Orders
    const [searchOrderIdCatering, setSearchOrderIdCatering] = useState(''); // Search for Catering Orders
    const [searchOrderIdOnline, setSearchOrderIdOnline] = useState(''); // Search for Online Orders
    const [searchOrganizationOnline, setSearchOrganizationOnline] = useState(''); // Filter by organization name
    const [filterDropdownNew, setFilterDropdownNew] = useState('saved'); // Default: NEW/saved orders only
    const [filterDropdownDelivered, setFilterDropdownDelivered] = useState('delivered'); // Dropdown filter for Delivered Orders
    const [filterDropdownCatering, setFilterDropdownCatering] = useState('orders'); // legacy; synced with cateringOrderType
    const [onlineOrderTypeFilter, setOnlineOrderTypeFilter] = useState('DELIVERY'); // Online order type filter: DELIVERY, PICKUP (MyEats only)
    const activeOnlineSource = getOnlineSourceFilter(orderTypeFilter);
    /** Junior Eats shows all order types — no delivery/pickup dropdown. */
    const effectiveOnlineOrderTypeFilter =
        orderTypeFilter === 'jreats' ? 'all' : onlineOrderTypeFilter;
    const [offlineOrderType, setOfflineOrderType] = useState(() =>
        normalizeOfflineOrderView(propOfflineOrderType)
    ); // 'orders' | 'orderHistory' (legacy save/delivered mapped on init)
    const [dineInActiveStatusSelections, setDineInActiveStatusSelections] = useState(
        buildDineInActiveStatusSelectionsDefault
    );
    const [dineInHistoryStatusSelections, setDineInHistoryStatusSelections] = useState(() =>
        buildEmptyStatusSelections(DINE_IN_HISTORY_STATUS_OPTS)
    );
    const [cateringOrderType, setCateringOrderType] = useState(() =>
        normalizeCateringOrderView(propCateringOrderType)
    ); // 'orders' | 'orderHistory'
    const [partyHallOrderType, setPartyHallOrderType] = useState(() =>
        propPartyHallOrderType === 'orders' ? 'orders' : 'quotes'
    );
    const [cateringActiveStatusSelections, setCateringActiveStatusSelections] = useState(
        buildCateringActiveStatusSelectionsDefault
    );
    const [cateringHistoryStatusSelections, setCateringHistoryStatusSelections] = useState(() =>
        buildEmptyStatusSelections(CATERING_HISTORY_STATUS_OPTS)
    );
    const [isLoadingOnlineOrders, setIsLoadingOnlineOrders] = useState(false);
    const [cachedOnlineOrders, setCachedOnlineOrders] = useState([]);
    const [cachedOnlineRestaurantId, setCachedOnlineRestaurantId] = useState(null);
    const [cachedOnlineType, setCachedOnlineType] = useState(null);
    const [onlineOrderStatusSelections, setOnlineOrderStatusSelections] = useState(
        buildOnlineOrderStatusSelectionsDefault
    );
    const [onlineOrderHistoryStatusSelections, setOnlineOrderHistoryStatusSelections] = useState(() =>
        buildEmptyStatusSelections(ORDER_HISTORY_ALL_OPTS)
    );
    const [historyFromDate, setHistoryFromDate] = useState(defaultOrderHistoryFromDate);
    const [historyToDate, setHistoryToDate] = useState(defaultOrderHistoryToDate);
    const [jreatsMealFilter, setJreatsMealFilter] = useState('');
    const [jreatsSchoolSelections, setJreatsSchoolSelections] = useState({});
    const [jreatsStatusSelections, setJreatsStatusSelections] = useState(
        buildJreatsOrderStatusSelectionsDefault
    );
    const [jreatsHistoryStatusSelections, setJreatsHistoryStatusSelections] = useState(
        buildJreatsHistoryStatusSelectionsDefault
    );
    const [dineInSort, setDineInSort] = useState(null);
    const [cateringSort, setCateringSort] = useState(null);
    const [onlineSort, setOnlineSort] = useState(null);
    const [isAssignAgentsPopupVisible, setIsAssignAgentsPopupVisible] = useState(false);
    const [assignAgentsSchool, setAssignAgentsSchool] = useState('');
    const [assignAgentsSelectedId, setAssignAgentsSelectedId] = useState('');
    const [assignAgentsSaving, setAssignAgentsSaving] = useState(false);

    // Deep-link from the Dine-In "active orders" strip: pre-filter to one status
    // for the selected channel and show the active (not history) view.
    useEffect(() => {
        if (!initialActiveStatus) return;
        const canon = resolveStatusKey(initialActiveStatus);
        if (orderTypeFilter === 'offline') {
            const code = DINE_IN_CANON_TO_CODE[canon];
            if (code) {
                setOfflineOrderType('orders');
                setDineInActiveStatusSelections(
                    buildSingleStatusSelection(DINE_IN_ACTIVE_STATUS_OPTS, code)
                );
            }
        } else if (orderTypeFilter === 'myeats' || orderTypeFilter === 'online') {
            const code =
                orderTypeFilter === 'myeats' && canon === 'ACCEPTED'
                    ? 'NEW'
                    : ONLINE_CANON_TO_CODE[canon];
            if (code) {
                const opts = getOnlineOrderStatusCheckboxOptionsForType('all', orderTypeFilter);
                if (setOnlineOrderType) setOnlineOrderType('orders');
                setOnlineOrderStatusSelections(buildSingleStatusSelection(opts, code));
            }
        } else if (orderTypeFilter === 'jreats') {
            const code = JREATS_CANON_TO_CODE[canon];
            if (code) {
                if (setOnlineOrderType) setOnlineOrderType('orders');
                setJreatsStatusSelections(
                    buildSingleStatusSelection(JREATS_ORDER_STATUS_OPTS, code)
                );
            }
        } else if (orderTypeFilter === 'catering') {
            const code = CATERING_CANON_TO_CODE[canon];
            if (code) {
                setCateringOrderType('orders');
                setCateringActiveStatusSelections(
                    buildSingleStatusSelection(CATERING_ACTIVE_STATUS_OPTS, code)
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialActiveStatus, orderTypeFilter]);

    const { handleSortAsc: handleDineInSortAsc, handleSortDesc: handleDineInSortDesc } =
        buildSortHandlers(setDineInSort, () => {
            setCurrentPageNew(1);
            setCurrentPageDelivered(1);
        });
    const { handleSortAsc: handleCateringSortAsc, handleSortDesc: handleCateringSortDesc } =
        buildSortHandlers(setCateringSort, () => setCurrentPageCatering(1));
    const { handleSortAsc: handleOnlineSortAsc, handleSortDesc: handleOnlineSortDesc } =
        buildSortHandlers(setOnlineSort, () => setCurrentPageOnline(1));

    const toggleOnlineOrderStatusFilter = (code) => {
        setOnlineOrderStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const toggleOnlineOrderHistoryStatusFilter = (code) => {
        setOnlineOrderHistoryStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const setAllOnlineOrderStatuses = (checked) => {
        const opts = getOnlineOrderStatusCheckboxOptionsForType(
            effectiveOnlineOrderTypeFilter,
            orderTypeFilter
        );
        setOnlineOrderStatusSelections((prev) => {
            const next = { ...prev };
            opts.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const setAllOnlineOrderHistoryStatuses = (checked) => {
        const opts = getOrderHistoryStatusCheckboxOptions(
            effectiveOnlineOrderTypeFilter,
            orderTypeFilter
        );
        setOnlineOrderHistoryStatusSelections((prev) => {
            const next = { ...prev };
            opts.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const toggleCateringActiveStatusFilter = (code) => {
        setCateringActiveStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const toggleCateringHistoryStatusFilter = (code) => {
        setCateringHistoryStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const setAllCateringActiveStatuses = (checked) => {
        setCateringActiveStatusSelections((prev) => {
            const next = { ...prev };
            CATERING_ACTIVE_STATUS_OPTS.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const setAllCateringHistoryStatuses = (checked) => {
        setCateringHistoryStatusSelections((prev) => {
            const next = { ...prev };
            CATERING_HISTORY_STATUS_OPTS.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const toggleDineInActiveStatusFilter = (code) => {
        setDineInActiveStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const toggleDineInHistoryStatusFilter = (code) => {
        setDineInHistoryStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const setAllDineInActiveStatuses = (checked) => {
        setDineInActiveStatusSelections((prev) => {
            const next = { ...prev };
            DINE_IN_ACTIVE_STATUS_OPTS.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const setAllDineInHistoryStatuses = (checked) => {
        setDineInHistoryStatusSelections((prev) => {
            const next = { ...prev };
            DINE_IN_HISTORY_STATUS_OPTS.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const autoRefreshOrdersRef = useRef(null);

    // Update local state when prop changes
    useEffect(() => {
        setOfflineOrderType(normalizeOfflineOrderView(propOfflineOrderType));
    }, [propOfflineOrderType]);

    useEffect(() => {
        const view = normalizeCateringOrderView(propCateringOrderType);
        setCateringOrderType(view);
        setFilterDropdownCatering(view);
    }, [propCateringOrderType]);

    useEffect(() => {
        setPartyHallOrderType(propPartyHallOrderType === 'orders' ? 'orders' : 'quotes');
    }, [propPartyHallOrderType]);

    const isCateringHistoryViewActive =
        orderTypeFilter === 'catering' && isCateringHistoryView(cateringOrderType);

    const navigate = useNavigate();

    const handleCateringSectionChange = (section) => {
        if (section === 'quotes') {
            setCateringOrderType('quotes');
            setFilterDropdownCatering('quotes');
            navigate('/home?tab=orders&orderType=catering&cateringView=quotes');
            return;
        }
        setCateringOrderType('orders');
        setFilterDropdownCatering('orders');
        navigate('/home?tab=orders&orderType=catering&cateringView=orders');
    };

    const handlePartyHallSectionChange = (section) => {
        const view = section === 'orders' ? 'orders' : 'quotes';
        setPartyHallOrderType(view);
        navigate(`/home?tab=orders&orderType=partyHall&partyHallView=${view}`);
    };

    const handleCateringOrdersViewChange = (viewRaw) => {
        const view = normalizeCateringOrderView(viewRaw);
        setCateringOrderType(view);
        setFilterDropdownCatering(view);
        const cateringView = view === 'orderHistory' ? 'orderHistory' : 'orders';
        navigate(`/home?tab=orders&orderType=catering&cateringView=${cateringView}`);
    };

    // Use userId from props if provided, otherwise get from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = userId ?? user?.userId ?? user?.id ?? user?.User_Id ?? null;

    // Fetch tables list
    const fetchTables = async () => {
        // Mock table numbers with seat counts - same as PlaceOrder
        const tableData = [
            { number: 1, seats: 2 },
            { number: 2, seats: 4 },
            { number: 3, seats: 2 },
            { number: 4, seats: 6 },
            { number: 5, seats: 4 },
            { number: 6, seats: 8 },
            { number: 7, seats: 2 },
            { number: 8, seats: 4 },
            { number: 9, seats: 6 },
            { number: 10, seats: 4 }
        ];
        setTables(tableData);
    };

    // Prefetch online orders in background when on Offline/Catering - decreases loading time when user opens Online tab
    const prefetchOnlineOrders = async (restaurantId) => {
        if (!restaurantId) return;
        try {
            let apiOrders = await fetchRestaurantOrders(restaurantId);
            apiOrders = apiOrders.filter(order => {
                const tableNum = order.tableNumber ?? order.table_number ?? order.TableNumber;
                const hasTableNumber = tableNum !== null && tableNum !== undefined && tableNum !== '' && String(tableNum).trim() !== '' && tableNum !== 0 && tableNum !== '0';
                if (hasTableNumber) return false;
                const orderStatus = (order.orderStatus || order.OrderStatus || order.order_status || '').toString().toUpperCase();
                const isCatering = order.orderType === 'catering' || order.eventDate || order.eventLocation || order.numberOfGuests;
                return isMyEatsActiveOrderStatus(orderStatus) && !isCatering;
            });
            const sorted = apiOrders
                .map((o) => normalizeRestaurantOrder(o))
                .sort((a, b) => new Date(b.createdDate || b.createdAt) - new Date(a.createdDate || a.createdAt));
            setCachedOnlineOrders(sorted);
            setCachedOnlineRestaurantId(restaurantId);
            setCachedOnlineType(`orders|${activeOnlineSource}|${effectiveOnlineOrderTypeFilter}`);
        } catch (e) { /* silent */ }
    };

    // Fetch orders function - shared by refresh, mount, and tab changes
    const fetchOrders = async (restaurantId, forceRefresh = false) => {
        try {
            // Get orders from localStorage (static orders)
            const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
            console.log('All local orders:', localOrders);
            let filteredLocalOrders = [];
            
            if (orderTypeFilter === 'offline') {
                // Dine-in: GET /restaurants/:id/orders + order-history (orderSource MB), merge with localStorage
                let apiOfflineOrders = [];
                if (restaurantId) {
                    try {
                        apiOfflineOrders = await fetchPosOrdersWithPayments(restaurantId);
                    } catch (apiErr) {
                        console.error('API fetch failed for dine-in orders:', apiErr?.response?.data || apiErr?.message);
                    }
                }

                // Local: orders with tableNumber OR saved/delivered, exclude catering
                filteredLocalOrders = localOrders.filter(order => {
                    const hasTableNumber = orderHasTableNumber(order);
                    const isCatering = order.orderType === 'catering' || order.eventDate || order.eventLocation || order.numberOfGuests;
                    if (isCatering) return false;
                    if (!isDineInMbOrder(order, { isDineInPosOrder })) return false;
                    const orderStatus = (order.orderStatus || order.OrderStatus || order.order_status || 'NEW').toString().toUpperCase();
                    const isSavedOrder = orderStatus === 'NEW';
                    const isTerminalOrder = isDineInHistoryOrderStatus(
                        normalizeOnlineOrderStatusForFilter(orderStatus)
                    );
                    if (!isDineInPosOrder(order) && !isSavedOrder && !isTerminalOrder) return false;
                    if (restaurantId) {
                        const orderRestaurantId = order.restaurantId || order.RestaurantId;
                        return !orderRestaurantId || orderRestaurantId === restaurantId || String(orderRestaurantId) === String(restaurantId);
                    }
                    return true;
                });

                // Merge API orders with local: overlay local edits on API rows; in Order History
                // do not add local-only rows (they are not on the server).
                const completedStatuses = ['DELIVERED', 'COMPLETED', 'PAID', 'COMPLETE'];
                const isHistoryView = isDineInOfflineHistoryView(offlineOrderType);
                const orderMap = new Map();
                apiOfflineOrders.forEach((o) => orderMap.set(o.orderId ?? o.orderNumber, { ...o }));
                filteredLocalOrders.forEach((o) => {
                    const id = o.orderId ?? o.orderNumber;
                    const localStatus = (o.orderStatus || o.OrderStatus || '').toString().toUpperCase();
                    const existing = orderMap.get(id);
                    if (existing) {
                        const localItems = o.orderLineItems ?? o.OrderLineItems ?? existing.orderLineItems;
                        const hasLocalItems = Array.isArray(localItems) && localItems.length > 0;
                        const merged = {
                            ...existing,
                            orderStatus: completedStatuses.includes(localStatus) ? localStatus : existing.orderStatus,
                            orderLineItems: hasLocalItems ? localItems : existing.orderLineItems,
                            OrderLineItems: hasLocalItems ? localItems : existing.OrderLineItems,
                            totalPrice: (o.totalPrice != null && o.totalPrice !== '') ? parseFloat(o.totalPrice) : existing.totalPrice,
                            customerName: (o.customerName && o.customerName !== 'N/A') ? o.customerName : existing.customerName,
                            mobile: o.mobile ?? o.customerMobile ?? o.contactNumber ?? existing.mobile,
                            customerMobile: o.mobile ?? o.customerMobile ?? o.contactNumber ?? existing.customerMobile,
                            // Overlay payment method from local order so table shows actual method (Paytm, PhonePe, etc.)
                            paymentMethod: o.paymentMethod ?? existing.paymentMethod,
                            paymentStatus: o.paymentStatus ?? existing.paymentStatus,
                            createdById: existing.createdById ?? o.createdById ?? null,
                            // Overlay table number from local order when backend does not send it
                            tableNumber: o.tableNumber ?? existing.tableNumber ?? null,
                        };
                        orderMap.set(id, merged);
                    } else if (!isHistoryView) {
                        orderMap.set(id, o);
                    }
                });
                filteredLocalOrders = Array.from(orderMap.values());
                console.log('Filtered offline orders:', filteredLocalOrders);
            } else if (orderTypeFilter === 'catering') {
                if (isCateringQuotesView(cateringOrderType)) {
                    return;
                }
                // For catering orders: fetch from API when restaurantId available, merge with localStorage
                let effectiveRestaurantId = restaurantId || selectedRestaurant || localStorage.getItem('restaurantId');
                // Fallback: fetch user's restaurants and use first one if none available (ensures DB orders load)
                if (!effectiveRestaurantId && currentUserId) {
                    try {
                        const token = localStorage.getItem('Token');
                        const restRes = await axiosRestaurantInstance.get(`/user/${currentUserId}/restaurants`, {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                        });
                        const list = Array.isArray(restRes?.data) ? restRes.data : [];
                        if (list.length > 0) {
                            effectiveRestaurantId = list[0].RestaurantId ?? list[0].restaurantId;
                            if (effectiveRestaurantId) localStorage.setItem('restaurantId', String(effectiveRestaurantId));
                        }
                    } catch (e) {
                        console.error('Fetch user restaurants for catering:', e?.response?.data ?? e?.message);
                    }
                }
                let apiCateringOrders = [];
                if (effectiveRestaurantId) {
                    try {
                        const token = localStorage.getItem('Token');
                        const response = await axiosRestaurantInstance.get(`/catering/orders/${effectiveRestaurantId}`, {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                        });
                        // Backend may return { orders: [...] } or array directly
                        const ordersData = response?.data?.orders ?? response?.data;
                        const rawApiOrders = Array.isArray(ordersData) ? ordersData : [];
                        apiCateringOrders = rawApiOrders.map((o) => {
                            const lineItems = (o.lineItems || []).map((li) => ({
                                foodItemId: li.cateringItemId ?? li.Food_Item_Id,
                                foodItemName: li.itemName ?? li.Item_Name ?? li.foodItemName,
                                foodQuantity: li.quantity ?? li.Quantity ?? li.foodQuantity ?? 1,
                                price: parseFloat(li.price || 0),
                                itemName: li.itemName ?? li.Item_Name ?? li.foodItemName,
                                quantity: li.quantity ?? li.Quantity ?? li.foodQuantity ?? 1,
                            }));
                            return {
                                orderId: o.cateringOrderId ?? o.orderId ?? o.Order_Id,
                                orderNumber: o.cateringOrderId ?? o.orderId ?? o.Order_Id,
                                cateringOrderId: o.cateringOrderId,
                                customerName: o.customerName ?? o.Customer_Name ?? 'N/A',
                                mobile: o.customerContact ?? o.customerMobile ?? o.Contact_Number,
                                customerMobile: o.customerContact ?? o.customerMobile,
                                contactNumber: o.customerContact ?? o.customerMobile,
                                altNumber: o.altNumber ?? o.Alt_Number,
                                eventDate: o.eventDate ?? o.Event_Date,
                                eventTime: o.eventTime ?? o.Event_Time,
                                eventType: o.eventType ?? o.Event_Type,
                                eventLocation: o.deliveryAddress ?? o.eventLocation ?? o.Event_Location,
                                deliveryAddress: o.deliveryAddress,
                                deliveryInstructions: o.deliveryInstructions ?? o.Delivery_Instructions,
                                orderLineItems: lineItems,
                                lineItems,
                                totalPrice: parseFloat(o.totalPrice || 0),
                                orderStatusCode: (
                                    o.orderStatusCode ??
                                    o.Order_Status_Code ??
                                    o.order_status_code ??
                                    'NEW'
                                )
                                    .toString()
                                    .toUpperCase(),
                                orderStatus: (
                                    o.orderStatus ??
                                    o.Order_Status ??
                                    o.orderStatusCode ??
                                    'NEW'
                                )
                                    .toString()
                                    .trim(),
                                orderType: 'catering',
                                restaurantId: o.restaurantId ?? o.Restaurant_Id,
                                restaurantName: o.restaurantName ?? o.Restaurant_Name,
                                createdDate: o.createdDate ?? o.createdAt ?? o.CreatedDate,
                                advancePayment: o.advancePayment ?? o.Advance_Payment,
                                pendingAmount: o.pendingAmount ?? o.Pending_Amount,
                                paymentMethod: o.paymentMethod ?? o.Payment_Method ?? o.payment_method,
                                numberOfGuests: o.numberOfGuests ?? o.Number_Of_Guests ?? o.number_of_guests,
                                deliveryContactName: o.deliveryContactName ?? o.Delivery_Contact_Name,
                            };
                        });
                    } catch (apiErr) {
                        console.error('API fetch failed for catering orders:', apiErr?.response?.data || apiErr?.message);
                    }
                }
                filteredLocalOrders = localOrders.filter(order => {
                    const isCatering = order.orderType === 'catering' || order.eventDate || order.eventLocation || order.numberOfGuests;
                    if (effectiveRestaurantId) {
                        const orderRestaurantId = order.restaurantId || order.RestaurantId;
                        const matchesRestaurant = !orderRestaurantId || orderRestaurantId === effectiveRestaurantId || String(orderRestaurantId) === String(effectiveRestaurantId);
                        return isCatering && matchesRestaurant;
                    }
                    return isCatering;
                });
                const completedStatuses = ['DELIVERED', 'COMPLETED', 'PAID', 'COMPLETE'];
                const orderMap = new Map();
                const toKey = (id) => String(id ?? '');
                apiCateringOrders.forEach((o) => {
                    const key = toKey(o.orderId ?? o.orderNumber ?? o.cateringOrderId);
                    if (key) orderMap.set(key, { ...o });
                });
                filteredLocalOrders.forEach((o) => {
                    const id = toKey(o.orderId ?? o.orderNumber);
                    if (!id) return;
                    const localStatus = (o.orderStatus || o.OrderStatus || '').toString().toUpperCase();
                    const existing = orderMap.get(id);
                    if (existing) {
                        // Always merge local edits (orderLineItems, totalPrice, advancePayment, etc.) so table reflects user updates
                        const localItems = o.orderLineItems || o.OrderLineItems || o.lineItems;
                        const hasLocalItems = Array.isArray(localItems) && localItems.length > 0;
                        const merged = {
                            ...existing,
                            orderStatus: completedStatuses.includes(localStatus) ? localStatus : existing.orderStatus,
                            orderLineItems: hasLocalItems ? localItems : existing.orderLineItems,
                            lineItems: hasLocalItems ? localItems : existing.lineItems,
                            totalPrice: (o.totalPrice != null && o.totalPrice !== '') ? parseFloat(o.totalPrice) : existing.totalPrice,
                            advancePayment: (o.advancePayment ?? o.advance_payment) != null ? (o.advancePayment ?? o.advance_payment) : existing.advancePayment,
                            pendingAmount: (o.pendingAmount ?? o.pending_amount) != null ? (o.pendingAmount ?? o.pending_amount) : existing.pendingAmount,
                            customerName: (o.customerName && o.customerName !== 'N/A') ? o.customerName : existing.customerName,
                            mobile: o.mobile ?? o.customerMobile ?? o.contactNumber ?? existing.mobile,
                            contactNumber: o.contactNumber ?? o.mobile ?? o.customerMobile ?? existing.contactNumber,
                            altNumber: o.altNumber ?? o.Alt_Number ?? existing.altNumber,
                            eventLocation: o.eventLocation ?? o.deliveryAddress ?? o.EventLocation ?? existing.eventLocation,
                            numberOfGuests: (o.numberOfGuests ?? o.NumberOfGuests ?? o.number_of_guests) != null ? (o.numberOfGuests ?? o.NumberOfGuests ?? o.number_of_guests) : existing.numberOfGuests,
                            paymentMethod: (o.paymentMethod ?? o.payment_method) != null ? (o.paymentMethod ?? o.payment_method) : existing.paymentMethod,
                        };
                        orderMap.set(id, merged);
                    } else {
                        orderMap.set(id, o);
                    }
                });
                const mergedCateringOrders = Array.from(orderMap.values());
                const sortedOrders = mergedCateringOrders.sort(
                    (a, b) => new Date(b.createdDate || b.createdAt || b.CreatedDate || 0) - new Date(a.createdDate || a.createdAt || a.CreatedDate || 0)
                );
                const initialAssignedAgents = {};
                sortedOrders.forEach(order => {
                    if (order.deliveryContactName) {
                        initialAssignedAgents[order.orderId] = order.deliveryContactName;
                    }
                });
                setOrders(sortedOrders);
                setAssignedAgents(initialAssignedAgents);
                return;
            } else {
                // For online orders: show cached data immediately if available (faster load), else Loading
                filteredLocalOrders = [];
                const onlineCacheKey = `${onlineOrderType || 'orders'}|${activeOnlineSource}|${effectiveOnlineOrderTypeFilter}`;
                const hasCache = !forceRefresh && cachedOnlineRestaurantId === restaurantId && cachedOnlineType === onlineCacheKey && cachedOnlineOrders.length > 0;
                if (hasCache) {
                    const canonCached = cachedOnlineOrders.map(withCanonicalOnlineDeliveryFields);
                    setOrders(canonCached);
                    const agents = {};
                    canonCached.forEach((o) => {
                        const id = resolveOrderId(o);
                        if (id && o.deliveryContactName) agents[id] = o.deliveryContactName;
                    });
                    setAssignedAgents(agents);
                    setIsLoadingOnlineOrders(false);
                } else {
                    setIsLoadingOnlineOrders(true);
                }

                let apiOrders = [];
            try {
                    if (restaurantId) {
                        if (onlineOrderType === 'orderHistory') {
                            const [historyOrders, activeOrders] = await Promise.all([
                                fetchRestaurantOrderHistory(restaurantId).catch(() => []),
                                fetchRestaurantOrders(restaurantId).catch(() => []),
                            ]);
                            const merged = [...historyOrders, ...activeOrders];
                            const seen = new Set();
                            apiOrders = merged.filter((order) => {
                                const oid = resolveOrderId(order);
                                if (!oid || seen.has(oid)) return false;
                                seen.add(oid);
                                return true;
                            });
                        } else if (orderTypeFilter === 'jreats') {
                            const [activeOrders, historyOrders] = await Promise.all([
                                fetchRestaurantOrders(restaurantId).catch(() => []),
                                fetchRestaurantOrderHistory(restaurantId).catch(() => []),
                            ]);
                            const todayTerminal = historyOrders.filter(isJreatsTodayTerminalOrder);
                            const merged = [...activeOrders, ...todayTerminal];
                            const seen = new Set();
                            apiOrders = merged.filter((order) => {
                                const oid = resolveOrderId(order);
                                if (!oid || seen.has(oid)) return false;
                                seen.add(oid);
                                return true;
                            });
                        } else {
                            apiOrders = await fetchRestaurantOrders(restaurantId);
                        }

                        apiOrders = apiOrders.filter((order) => {
                            const tableNum = order.tableNumber ?? order.table_number ?? order.TableNumber;
                            const hasTableNumber = tableNum !== null &&
                                                 tableNum !== undefined &&
                                                 tableNum !== '' &&
                                                 String(tableNum).trim() !== '' &&
                                                 tableNum !== 0 &&
                                                 tableNum !== '0';

                            if (hasTableNumber) {
                                return false;
                            }

                            const orderStatus = (order.orderStatus || order.OrderStatus || order.order_status || '').toString().toUpperCase();
                            const isCatering = order.orderType === 'catering' || order.eventDate || order.eventLocation || order.numberOfGuests;
                            const source = String(order.orderSource || order.Order_Source || '').toUpperCase();
                            const ordType = String(order.orderType || order.Order_Type || '').toUpperCase();
                            const sourceOk = source === activeOnlineSource;
                            const typeOk =
                                effectiveOnlineOrderTypeFilter === 'all' ||
                                ordType === effectiveOnlineOrderTypeFilter;

                            if (onlineOrderType === 'orderHistory') {
                                const statusNorm = normalizeOnlineOrderStatusForFilter(
                                    order.orderStatus || order.OrderStatus || order.order_status
                                );
                                const allowedHist = allowedOrderHistoryStatusesForType(
                                    effectiveOnlineOrderTypeFilter,
                                    orderTypeFilter
                                );
                                const isTerminal = allowedHist.includes(statusNorm);
                                const isPastUndelivered = orderMatchesUndeliveredHistoryFilter(order);
                                if (orderTypeFilter === 'jreats' && isJreatsTodayTerminalOrder(order)) {
                                    return false;
                                }
                                return (
                                    (isTerminal || isPastUndelivered) &&
                                    !isCatering &&
                                    sourceOk &&
                                    typeOk
                                );
                            }

                            if (orderTypeFilter === 'jreats') {
                                return !isCatering && sourceOk && typeOk;
                            }
                            const isValidStatus = isMyEatsActiveOrderStatus(orderStatus);
                            return isValidStatus && !isCatering && sourceOk && typeOk;
                        });
                    }
                } catch (apiError) {
                    console.error('API fetch failed for online orders:', apiError?.response?.data || apiError?.message);
                    setIsLoadingOnlineOrders(false);
                }

                // Combine local and API orders for online only
                const allOrders = [...filteredLocalOrders, ...apiOrders];
                const uniqueOrders = allOrders.filter((order, index, self) => {
                    const orderId = resolveOrderId(order);
                    return index === self.findIndex((o) => resolveOrderId(o) === orderId);
                });

                const sortedOrders = uniqueOrders
                    .sort(
                        (a, b) =>
                            new Date(b.createdDate || b.createdAt) -
                            new Date(a.createdDate || a.createdAt)
                    )
                    .map(withCanonicalOnlineDeliveryFields);

                // Map orders and initialize assignedAgents state with fetched data
                const initialAssignedAgents = {};
                sortedOrders.forEach(order => {
                    const oid = resolveOrderId(order);
                    if (oid && order.deliveryContactName) {
                        initialAssignedAgents[oid] = order.deliveryContactName;
                    }
                });
        
                setOrders(sortedOrders);
                setAssignedAgents(initialAssignedAgents);
                setIsLoadingOnlineOrders(false);
                setCachedOnlineOrders(sortedOrders);
                setCachedOnlineRestaurantId(restaurantId);
                setCachedOnlineType(`${onlineOrderType || 'orders'}|${activeOnlineSource}|${effectiveOnlineOrderTypeFilter}`);
                return;
            }

            // For offline orders: already merged API + localStorage above
            const sortedOrders = filteredLocalOrders.sort(
                (a, b) => new Date(b.createdDate || b.createdAt || b.CreatedDate) - new Date(a.createdDate || a.createdAt || a.CreatedDate)
            );
    
            console.log('Sorted offline orders:', sortedOrders);
    
            // Map orders and initialize assignedAgents state
            const initialAssignedAgents = {};
            sortedOrders.forEach(order => {
                if (order.deliveryContactName) {
                    initialAssignedAgents[order.orderId] = order.deliveryContactName;
                }
            });
    
            setOrders(sortedOrders);
            setAssignedAgents(initialAssignedAgents);
            } catch (error) {
                console.error('Error fetching orders:', error.response?.data || error.message);
            }
        };

    autoRefreshOrdersRef.current = {
        orderTypeFilter,
        selectedRestaurant,
        fetchOrders,
        setIsLoadingOnlineOrders,
    };

    useEffect(() => {
        const EVERY_FIVE_MIN_MS = 5 * 60 * 1000;
        const id = window.setInterval(() => {
            const ctx = autoRefreshOrdersRef.current;
            if (!ctx?.fetchOrders) return;
            const rid = ctx.selectedRestaurant || localStorage.getItem('restaurantId') || null;
            if (isOnlineOrdersView(ctx.orderTypeFilter)) {
                if (rid) ctx.setIsLoadingOnlineOrders(true);
                ctx.fetchOrders(rid, true);
            } else {
                ctx.fetchOrders(rid);
            }
        }, EVERY_FIVE_MIN_MS);
        return () => window.clearInterval(id);
    }, []);

    // When switching to online orders: show cache immediately if available (faster), else Loading
    useEffect(() => {
        if (isOnlineOrdersView(orderTypeFilter)) {
            const onlineCacheKey = `${onlineOrderType || 'orders'}|${activeOnlineSource}|${effectiveOnlineOrderTypeFilter}`;
            const rid =
                selectedRestaurant || localStorage.getItem('restaurantId') || '';
            const hasCache =
                rid &&
                cachedOnlineRestaurantId === String(rid) &&
                cachedOnlineType === onlineCacheKey &&
                cachedOnlineOrders.length > 0;
            if (hasCache) {
                setOrders(cachedOnlineOrders);
                const agents = {};
                cachedOnlineOrders.forEach(o => { const id = o.orderId ?? o.OrderId; if (id && o.deliveryContactName) agents[id] = o.deliveryContactName; });
                setAssignedAgents(agents);
                setIsLoadingOnlineOrders(false);
            } else {
                setOrders([]);
                setIsLoadingOnlineOrders(true);
            }
        }
    }, [orderTypeFilter, onlineOrderType, activeOnlineSource, effectiveOnlineOrderTypeFilter]);

    useEffect(() => {
        const fetchRestaurants = async () => {
            if (!currentUserId) {
                console.error('User ID is not available');
                return;
            }
    
            try {
                const token = localStorage.getItem('Token');
                const response = await axiosRestaurantInstance.get(`/user/${currentUserId}/restaurants`, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Set the token in the Authorization header
                    }
                });
                console.log('Fetched restaurants:', response.data);
                const restaurantsList = Array.isArray(response.data) ? response.data : [];
                setRestaurants(restaurantsList);
                
                if (restaurantsList.length > 0 && !selectedRestaurant) {
                    let restaurantIdToUse = null;
                    try {
                        const initial = await resolveInitialRestaurant(
                            currentUserId,
                            restaurantsList
                        );
                        if (initial) {
                            restaurantIdToUse = extractRestaurantId(initial);
                        }
                    } catch (resolveErr) {
                        console.warn('Could not resolve default restaurant:', resolveErr);
                    }
                    if (!restaurantIdToUse) {
                        const fallback = pickDefaultRestaurantFromList(restaurantsList);
                        restaurantIdToUse =
                            fallback?.RestaurantId ??
                            fallback?.restaurantId ??
                            fallback?.restaurant_id;
                    }
                    if (restaurantIdToUse) {
                        setSelectedRestaurant(restaurantIdToUse);
                        localStorage.setItem('restaurantId', String(restaurantIdToUse));
                        if (
                            orderTypeFilter === 'offline' ||
                            orderTypeFilter === 'catering' ||
                            orderTypeFilter === 'myeats' ||
                            orderTypeFilter === 'jreats'
                        ) {
                            fetchOrders(restaurantIdToUse);
                        }
                    }
                } else if (orderTypeFilter === 'catering' && selectedRestaurant) {
                    fetchOrders(selectedRestaurant);
                }
            } catch (error) {
                console.error('Error fetching restaurants:', error.response?.data || error.message);
            }
        };
    
        // Fetch restaurants once on component mount
        fetchRestaurants();
    
        // Fetch tables for offline orders
        if (orderTypeFilter === 'offline') {
            fetchTables();
        }
    
        if (isOnlineOrdersView(orderTypeFilter)) {
            const rid =
                selectedRestaurant ||
                localStorage.getItem('restaurantId') ||
                null;
            if (rid) {
                if (!selectedRestaurant) setSelectedRestaurant(String(rid));
                fetchOrders(rid);
            } else {
                setOrders([]);
                setIsLoadingOnlineOrders(true);
            }
        } else if (selectedRestaurant) {
            fetchOrders(selectedRestaurant);
        } else if (
            orderTypeFilter === 'offline' ||
            orderTypeFilter === 'catering'
        ) {
            // Use localStorage restaurantId so API is called; fetchOrders has fallback to fetch user restaurants
            fetchOrders(localStorage.getItem('restaurantId') || null);
        }
        // Prefetch online orders when on Offline/Catering so Online tab loads faster
        if (!isOnlineOrdersView(orderTypeFilter) && selectedRestaurant) {
            prefetchOnlineOrders(selectedRestaurant);
        }
    }, [currentUserId, selectedRestaurant, orderTypeFilter, onlineOrderType, activeOnlineSource, effectiveOnlineOrderTypeFilter]);

    // Refresh orders when dropdown selections change (onlineOrderType, cateringOrderType, offlineOrderType)
    // Also refresh when restaurant selection changes
    useEffect(() => {
        // Only refresh if we're already on the correct order type filter
        // This prevents unnecessary fetches when switching between order types
        const onlineRid = resolveRestaurantForFetch();
        if (isOnlineOrdersView(orderTypeFilter) && onlineRid && onlineOrderType) {
            fetchOrders(onlineRid);
        } else if (orderTypeFilter === 'catering' && cateringOrderType) {
            fetchOrders(selectedRestaurant || null);
        } else if (orderTypeFilter === 'offline' && offlineOrderType) {
            // Refresh offline orders when switching between "Saved" and "Delivered"
            if (selectedRestaurant) {
                fetchOrders(selectedRestaurant);
            } else {
                fetchOrders(null);
            }
        }
    }, [orderTypeFilter, onlineOrderType, cateringOrderType, offlineOrderType, selectedRestaurant, activeOnlineSource, effectiveOnlineOrderTypeFilter]);

    // Refresh orders when window gains focus (user navigates back to the page)
    useEffect(() => {
        const handleFocus = () => {
            if (orderTypeFilter === 'offline' || orderTypeFilter === 'catering') {
                fetchOrders(selectedRestaurant || null);
            } else if (isOnlineOrdersView(orderTypeFilter)) {
                fetchOrders(resolveRestaurantForFetch());
            } else if (selectedRestaurant) {
                fetchOrders(selectedRestaurant);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [selectedRestaurant, orderTypeFilter]);

    useEffect(() => {
        if (orderTypeFilter !== 'jreats') {
            setJreatsMealDeliveryTimes(null);
            return undefined;
        }
        const restaurantId =
            selectedRestaurant || localStorage.getItem('restaurantId') || null;
        if (!restaurantId) {
            setJreatsMealDeliveryTimes(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const times = await fetchRestaurantMealDeliveryTimes(restaurantId);
                if (!cancelled) setJreatsMealDeliveryTimes(times ?? {});
            } catch (err) {
                console.error(
                    'Failed to load Jr Eats meal delivery times:',
                    err?.response?.data ?? err?.message
                );
                if (!cancelled) setJreatsMealDeliveryTimes({});
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [orderTypeFilter, selectedRestaurant]);

    // Refresh orders when localStorage changes (when orders are completed/updated)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'localOrders') {
                // Refresh orders when localOrders changes
                if (orderTypeFilter === 'offline') {
                    if (selectedRestaurant) {
                        fetchOrders(selectedRestaurant);
                    } else {
                        fetchOrders(null);
                    }
                } else if (orderTypeFilter === 'catering') {
                    fetchOrders(selectedRestaurant || null);
                } else if (selectedRestaurant) {
                    fetchOrders(selectedRestaurant);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        // Also listen for custom event for same-tab updates
        window.addEventListener('localOrdersUpdated', handleStorageChange);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('localOrdersUpdated', handleStorageChange);
        };
    }, [selectedRestaurant, orderTypeFilter]);
    


const fetchDeliveryAgents = async () => {
    try {
        const restaurantId =
            selectedRestaurant ||
            localStorage.getItem('restaurantId') ||
            null;
        const params = {};
        if (restaurantId && restaurantId !== 'null' && restaurantId !== 'undefined') {
            params.restaurantId = restaurantId;
        }
        const response = await axiosRestaurantInstance.get(
            RESTAURANT_API_PATHS.restaurantsDeliveryAgents,
            { params }
        );
        const payload = response.data;
        const list = Array.isArray(payload?.users)
            ? payload.users
            : Array.isArray(payload?.Users)
              ? payload.Users
              : Array.isArray(payload)
                ? payload
                : [];
        setDeliveryAgents(list);
        return list;
    } catch (error) {
        const status = error?.response?.status;
        if (status === 804) {
            setDeliveryAgents([]);
            return [];
        }
        console.error('Error fetching delivery agents:', error.response?.data || error.message);
        setDeliveryAgents([]);
        return [];
    }
};

const resolveRestaurantForFetch = () =>
    selectedRestaurant || localStorage.getItem('restaurantId') || null;

const handleRefreshOrders = () => {
    const rid = resolveRestaurantForFetch();
    if (isOnlineOrdersView(orderTypeFilter)) {
        if (rid) setIsLoadingOnlineOrders(true);
        fetchOrders(rid, true);
        return;
    }
    fetchOrders(rid);
};

// Same pattern as offline: Created_By on line items when items are sent
const updateCateringOrderViaApi = async (orderId, payload) => {
    try {
        const token = localStorage.getItem('Token');
        const updatedBy = payload.updatedBy ?? (currentUserId ? Number(currentUserId) : 1);
        const createdBy = payload.createdBy ?? updatedBy;
        const rawItems = payload.cart ?? payload.lineItems ?? [];
        const lineItems = rawItems.map((item) => ({
            Catering_Item_Id: item.foodItemId ?? item.cateringItemId ?? item.id,
            Item_Name: item.foodItemName ?? item.itemName ?? item.name ?? 'Item',
            Quantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity, 10) || 1),
            Price: parseFloat(item.price || 0),
            Created_By: createdBy,
        }));
        const body = {
            orderStatus: payload.orderStatus ?? 'NEW',
            deliveryContactName: payload.deliveryContactName ?? null,
            deliveryContactNumber: payload.deliveryContactNumber ?? null,
            updatedBy,
        };
        if (lineItems.length > 0) {
            body.lineItems = lineItems.map((li) => ({
                cateringItemId: li.Catering_Item_Id,
                itemName: li.Item_Name,
                quantity: li.Quantity,
                price: li.Price,
                createdBy,
                updatedBy,
            }));
        }
        const res = await axiosRestaurantInstance.put(
            `/catering/order/${orderId}`,
            body,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        return res?.data ?? null;
    } catch (err) {
        console.error('Update catering order API error:', err?.response?.data ?? err?.message);
        throw err;
    }
};

const handleDeliveryAgentSelectChange = async (agentIdRaw) => {
    const agentId = agentIdRaw != null ? String(agentIdRaw) : '';
    setSelectedDeliveryAgentId(agentId);
    if (!agentId || currentOrderId == null) return false;

    const selectedAgent = deliveryAgents.find((a) => getDeliveryAgentRecordId(a) === agentId);
    if (!selectedAgent) return false;

    if (deliveryAssignSaving) return false;
    setDeliveryAssignSaving(true);

    /** Online assignment: echo API delivery_time onto local row. */
    let syncedDeliveryTimeForOrder = null;

    try {
        const token = localStorage.getItem('Token');
        const updatedById = currentUserId ? Number(currentUserId) : 1;

        if (orderTypeFilter === 'catering') {
            const endpoint = `/catering/order/${currentOrderId}`;
            const body = {
                orderStatus: 'OUT-FOR-DELIVERY',
                deliveryContactName: getDeliveryAgentDisplayName(selectedAgent),
                deliveryContactNumber: getDeliveryAgentPhone(selectedAgent) || null,
                updatedBy: updatedById,
            };
            const response = await axiosRestaurantInstance.put(endpoint, body, {
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log(response.data?.message);
        } else {
            const order_status_code = onlineDisplayStatusToOrderStatusCode(selectedStatusForUpdate);
            const orderRow = orders.find(
                (o) => o.orderId === currentOrderId || String(o.orderId) === String(currentOrderId)
            );
            const delivery_time = resolveOnlineDeliveryTimeForUpdate(
                orderRow,
                deliveryTimeModalLocal,
                orderTypeFilter,
                jreatsMealDeliveryTimes
            );
            syncedDeliveryTimeForOrder = delivery_time;
            const agentRowIdNum = Number(getDeliveryAgentRecordId(selectedAgent));
            const body = {
                order_status_code,
                delivery_contact_name: getDeliveryAgentDisplayName(selectedAgent),
                delivery_contact_phone: String(getDeliveryAgentPhone(selectedAgent) ?? ''),
                delivery_contact_id: agentRowIdNum,
                delivery_time,
                updated_by_id: updatedById,
            };
            const response = await axiosRestaurantInstance.put(
                `/restaurant/orders/${currentOrderId}`,
                body,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log(response.data?.message);
        }

        const chosenName = getDeliveryAgentDisplayName(selectedAgent);
        setAssignedAgents((prev) => ({
            ...prev,
            [currentOrderId]: chosenName,
        }));

        const assignDisplayStatus =
            orderTypeFilter === 'catering' ? 'OUT-FOR-DELIVERY' : selectedStatusForUpdate;

        setOrders((prevOrders) =>
            prevOrders.map((order) => {
                const oid = order.orderId;
                if (oid !== currentOrderId && String(oid) !== String(currentOrderId)) {
                    return order;
                }
                const agentIdPersist = getDeliveryAgentRecordId(selectedAgent);
                const patch = {
                    ...order,
                    deliveryContactName: chosenName,
                    deliveryContactNo: getDeliveryAgentPhone(selectedAgent),
                    ...(agentIdPersist
                        ? { deliveryContactId: Number(agentIdPersist), delivery_contact_id: Number(agentIdPersist) }
                        : {}),
                };
                if (isOnlineOrdersView(orderTypeFilter) || orderTypeFilter === 'catering') {
                    patch.orderStatus = assignDisplayStatus;
                }
                if (syncedDeliveryTimeForOrder != null) {
                    patch.deliveryTime = syncedDeliveryTimeForOrder;
                    patch.DeliveryTime = syncedDeliveryTimeForOrder;
                }
                return patch;
            })
        );

        setSelectedDeliveryAgentId(agentId);
        return true;
    } catch (error) {
        console.error('Error updating delivery assignment:', error.response?.data || error.message);
        window.alert(
            error.response?.data?.message ||
                'Could not assign delivery agent. Please try again.'
        );
        setSelectedDeliveryAgentId('');
        return false;
    } finally {
        setDeliveryAssignSaving(false);
    }
};

const handleDeliveryAgentModalPick = (agentIdRaw) => {
    setDeliveryAgentSearch('');
    const el = deliveryAgentDropdownRef.current;
    if (el) el.open = false;
    setSelectedDeliveryAgentId(String(agentIdRaw ?? ''));
};

const handleActionClick = async (orderOrId, action) => {
    const orderId =
        typeof orderOrId === 'object' && orderOrId != null
            ? resolveOrderId(orderOrId)
            : orderOrId;
    if (orderId == null || orderId === '') {
        window.alert(
            action === 'edit'
                ? 'Cannot edit this order: order ID is missing.'
                : 'Cannot cancel this order: order ID is missing.'
        );
        return;
    }

    if (action === "edit") {
        const isCatering = orderTypeFilter === 'catering';
        navigate(
            isCatering
                ? '/home?tab=orders&orderType=catering'
                : '/home?tab=dine-in',
            {
                state: {
                    editOrderId: orderId,
                    orderTypeFilter: orderTypeFilter,
                    orderType: orderTypeFilter,
                    activeTab: isCatering ? 'orders' : 'dine-in',
                },
            }
        );
    } else if (action === "cancel") {
        const confirmCancel = await confirmAction({
            title: 'Cancel this order?',
            message: `Order #${orderId} will be marked as cancelled. This cannot be undone.`,
            confirmLabel: 'Yes, cancel order',
            cancelLabel: 'Keep order',
            danger: true,
        });
        if (confirmCancel) {
            await handleCancelOrder(orderId);
        }
    }
};

const handleStatusChange = async (orderId, newStatus, deliveryTimeModalValue = '') => {
    if (!allowOrderStatusUpdate) return;
    let onlineDeliverySynced = null;

    // For catering orders, update via API
    if (orderTypeFilter === 'catering') {
        try {
            await updateCateringOrderViaApi(orderId, {
                orderStatus: newStatus,
                updatedBy: currentUserId ? Number(currentUserId) : 1,
            });
        } catch (err) {
            console.error('Update catering status failed:', err?.response?.data ?? err?.message);
            return;
        }
    } else if (isOnlineOrdersView(orderTypeFilter)) {
        try {
            const token = localStorage.getItem('Token');
            const orderRow = orders.find(
                (o) => o.orderId === orderId || String(o.orderId) === String(orderId)
            );
            const delivery_time = resolveOnlineDeliveryTimeForUpdate(
                orderRow,
                deliveryTimeModalValue,
                orderTypeFilter,
                jreatsMealDeliveryTimes
            );
            onlineDeliverySynced = delivery_time;
            await axiosRestaurantInstance.put(
                `/restaurant/orders/${orderId}`,
                {
                    order_status_code: onlineDisplayStatusToOrderStatusCode(newStatus),
                    updated_by_id: currentUserId ? Number(currentUserId) : 1,
                    delivery_time,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
        } catch (err) {
            console.error('Update online status failed:', err?.response?.data ?? err?.message);
            return;
        }
    } else if (orderTypeFilter === 'offline') {
        const orderIdNum = Number(orderId);
        if (Number.isFinite(orderIdNum) && orderIdNum > 0) {
            try {
                await updateRestaurantOrder(orderId, {
                    orderStatus: newStatus,
                    orderStatusCode: dineInDisplayStatusToOrderStatusCode(newStatus),
                    updatedById: currentUserId ? Number(currentUserId) : 1,
                });
            } catch (err) {
                console.error('Update dine-in status failed:', err?.response?.data ?? err?.message);
                return;
            }
        }
    }
    // Update order in localStorage
    const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
    const updatedOrders = localOrders.map(order => {
        if (order.orderId === orderId || String(order.orderId) === String(orderId)) {
            const statusCode =
                orderTypeFilter === 'catering'
                    ? cateringDisplayStatusToOrderStatusCode(newStatus)
                    : onlineDisplayStatusToOrderStatusCode(newStatus);
            const patch = {
                ...order,
                orderStatus: newStatus,
                OrderStatus: newStatus,
                order_status: newStatus,
                orderStatusCode: statusCode,
            };
            if (isOnlineOrdersView(orderTypeFilter) && onlineDeliverySynced != null) {
                patch.deliveryTime = onlineDeliverySynced;
            }
            return patch;
        }
        return order;
    });
    localStorage.setItem('localOrders', JSON.stringify(updatedOrders));

    // Update order in state
    setOrders((prevOrders) =>
        prevOrders.map((order) => {
            if (order.orderId !== orderId && String(order.orderId) !== String(orderId)) {
                return order;
            }
            const statusCode =
                orderTypeFilter === 'catering'
                    ? cateringDisplayStatusToOrderStatusCode(newStatus)
                    : onlineDisplayStatusToOrderStatusCode(newStatus);
            const patch = {
                ...order,
                orderStatus: newStatus,
                OrderStatus: newStatus,
                order_status: newStatus,
                orderStatusCode: statusCode,
            };
            if (isOnlineOrdersView(orderTypeFilter) && onlineDeliverySynced != null) {
                patch.deliveryTime = onlineDeliverySynced;
                patch.DeliveryTime = onlineDeliverySynced;
            }
            return patch;
        })
    );
};

const handleTableChange = (orderId, newTableNumber) => {
    // Update order in localStorage
    const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
    const updatedOrders = localOrders.map(order => {
        if (order.orderId === orderId) {
            return {
                ...order,
                tableNumber: newTableNumber || null
            };
        }
        return order;
    });
    localStorage.setItem('localOrders', JSON.stringify(updatedOrders));

    // Update order in state
    setOrders(prevOrders => prevOrders.map(order => 
        order.orderId === orderId
            ? { ...order, tableNumber: newTableNumber || null }
            : order
    ));
};

const handleCancelOrder = async (orderId) => {
    try {
        const response =
            orderTypeFilter === 'catering'
                ? await cancelCateringOrder(orderId)
                : await cancelRestaurantOrder(orderId);
        console.log(response?.message);

        // Update the specific order in the state
        setOrders((prevOrders) =>
            prevOrders.map((order) =>
                order.orderId === orderId || String(order.orderId) === String(orderId)
                    ? { ...order, orderStatus: 'CANCELLED', orderStatusCode: 'CAN' }
                    : order
            )
        );
        // Update localStorage so cancelled order shows as CANCELLED
        const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
        const updated = localOrders.map((order) =>
            order.orderId === orderId || String(order.orderId) === String(orderId)
                ? { ...order, orderStatus: 'CANCELLED', orderStatusCode: 'CAN' }
                : order
        );
        localStorage.setItem('localOrders', JSON.stringify(updated));
        window.dispatchEvent(new Event('localOrdersUpdated'));
    } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;
        console.error('Error cancelling order:', status ? `HTTP ${status}` : '', data || error.message);
        window.alert(
            data?.error || data?.message || 'Failed to cancel order. Please try again.'
        );
    }
};

    const handleClosePopup = () => {
        setIsPopupVisible(false);
        setSelectedDeliveryAgentId('');
        setDeliveryAssignSaving(false);
        setDeliveryTimeModalLocal('');
        setDeliveryAgentSearch('');
    };

    const handleFoodItemsClick = (order) => {
        setSelectedOrderItems(selectedOrderItems === order.orderId ? null : order.orderId);
    };

    const handleOrderIdClick = (orderId) => {
        const fromOrderView =
            isOnlineOrdersView(orderTypeFilter) ? 'online'
            : orderTypeFilter === 'catering' ? 'catering'
            : 'offline';
        const fromOrderHistory =
            (isOnlineOrdersView(orderTypeFilter) && onlineOrderType === 'orderHistory') ||
            (orderTypeFilter === 'offline' && isDineInOfflineHistoryView(offlineOrderType)) ||
            (orderTypeFilter === 'catering' && cateringOrderType === 'orderHistory');
        navigate(`/OrderDetails/${resolveRestaurantForFetch()}/${orderId}`, {
            state: {
                fromOrderView,
                ...(fromOrderHistory ? { fromOrderHistory: true } : {}),
            },
        });
    };

    const handleOpenStatusUpdateModal = async (orderId, currentStatus, presetStatus = null) => {
        if (!allowOrderStatusUpdate) return;
        const statusSource = presetStatus != null ? presetStatus : currentStatus;
        const orderRow = orders.find(
            (o) => o.orderId === orderId || String(o.orderId) === String(orderId)
        );
        setCurrentOrderId(orderId);
        setSelectedStatusForUpdate(
            orderTypeFilter === 'offline'
                ? dineInStatusForSelect(statusSource)
                : orderTypeFilter === 'catering'
                  ? cateringStatusForSelect(statusSource)
                  : orderTypeFilter === 'jreats'
                    ? jreatsStatusForSelect(statusSource)
                    : onlineStatusForSelect(statusSource)
        );
        setSelectedDeliveryAgentId('');
        setDeliveryAgentSearch('');
        setDeliveryAssignSaving(false);
        setIsPopupVisible(true);
        let mealTimes = jreatsMealDeliveryTimes;
        if (orderTypeFilter === 'jreats') {
            const restaurantId = resolveRestaurantForFetch();
            if (mealTimes == null && restaurantId) {
                try {
                    mealTimes = await fetchRestaurantMealDeliveryTimes(restaurantId);
                    setJreatsMealDeliveryTimes(mealTimes ?? {});
                } catch (err) {
                    console.error(
                        'Failed to load Jr Eats meal delivery times:',
                        err?.response?.data ?? err?.message
                    );
                    mealTimes = {};
                    setJreatsMealDeliveryTimes({});
                }
            }
        }
        const apiDt =
            orderTypeFilter === 'jreats'
                ? buildJreatsApiDeliveryTime(orderRow, mealTimes ?? {})
                : formatDeliveryTimeForUpdateApi(deliveryTimeRawFromOrder(orderRow)) ??
                  deliveryTimeNowForUpdateApi();
        setDeliveryTimeModalLocal(toDatetimeLocalInputValue(apiDt));
        let agentsList = [];
        if (isOnlineOrdersView(orderTypeFilter)) {
            const isPickup =
                isMyEatsOrdersView(orderTypeFilter) && isPickupOnlineOrder(orderRow);
            if (!isPickup) {
                agentsList = await fetchDeliveryAgents();
            }
        }
        const resolvedAgentId = resolveAssignedAgentRecordId(orderRow, agentsList);
        if (resolvedAgentId !== '') {
            setSelectedDeliveryAgentId(resolvedAgentId);
        }
    };

    const handleSubmitStatusUpdate = async () => {
        if (!currentOrderId) return;

        const orderRow = orders.find(
            (o) => o.orderId === currentOrderId || String(o.orderId) === String(currentOrderId)
        );
        const isPickupModalOrder =
            isMyEatsOrdersView(orderTypeFilter) && isPickupOnlineOrder(orderRow);

        if (
            orderTypeFilter === 'myeats' &&
            resolveStatusKey(selectedStatusForUpdate) === 'READY' &&
            !isPickupModalOrder
        ) {
            if (!deliveryTimeModalLocal.trim()) {
                window.alert('Please set delivery time.');
                return;
            }
            if (!isPickupModalOrder && !selectedDeliveryAgentId) {
                window.alert('Please select a delivery agent.');
                return;
            }
        }

        if (
            orderTypeFilter === 'jreats' &&
            resolveStatusKey(selectedStatusForUpdate) === 'OFD' &&
            !selectedDeliveryAgentId
        ) {
            window.alert('Please select a delivery agent.');
            return;
        }

        let saved = true;
        if (
            isOnlineOrdersView(orderTypeFilter) &&
            selectedDeliveryAgentId &&
            !isPickupModalOrder
        ) {
            saved = await handleDeliveryAgentSelectChange(selectedDeliveryAgentId);
        } else {
            await handleStatusChange(
                currentOrderId,
                selectedStatusForUpdate,
                deliveryTimeModalLocal
            );
        }

        if (!saved) return;

        setIsPopupVisible(false);
        setDeliveryTimeModalLocal('');
        setDeliveryAgentSearch('');
    };

    const handleOpenAssignAgentsModal = async () => {
        setAssignAgentsSchool('');
        setAssignAgentsSelectedId('');
        setAssignAgentsSaving(false);
        setIsAssignAgentsPopupVisible(true);
        await fetchDeliveryAgents();
    };

    const handleCloseAssignAgentsPopup = () => {
        setIsAssignAgentsPopupVisible(false);
        setAssignAgentsSchool('');
        setAssignAgentsSelectedId('');
        setAssignAgentsSaving(false);
    };

    const handleAssignAgentsSubmit = async () => {
        if (!assignAgentsSchool) {
            window.alert('Please select a school.');
            return;
        }
        if (!assignAgentsSelectedId) {
            window.alert('Please select a delivery agent.');
            return;
        }
        const agent = deliveryAgents.find(
            (a) => getDeliveryAgentRecordId(a) === assignAgentsSelectedId
        );
        if (!agent) return;

        const schoolOrders = orders.filter((order) => {
            if (!isOrderCreatedToday(order)) return false;
            const orgName = String(order.organizationName ?? order.OrganizationName ?? '').trim();
            if (orgName !== assignAgentsSchool) return false;
            const status = normalizeOnlineOrderStatusForFilter(
                order.orderStatus || order.OrderStatus || order.order_status
            );
            const excluded = ['CANCELLED', 'DELIVERED', 'COMPLETED', 'PAID'];
            return !excluded.includes(status);
        });

        if (schoolOrders.length === 0) {
            window.alert('No active orders found for the selected school.');
            return;
        }

        setAssignAgentsSaving(true);
        const token = localStorage.getItem('Token');
        const updatedById = currentUserId ? Number(currentUserId) : 1;
        const updatedAssigned = { ...assignedAgents };
        const orderUpdates = [];

        try {
            for (let i = 0; i < schoolOrders.length; i += 1) {
                const orderRow = schoolOrders[i];
                const orderId = resolveOrderId(orderRow);
                const order_status_code = onlineDisplayStatusToOrderStatusCode(
                    jreatsStatusForSelect(orderRow.orderStatus || orderRow.OrderStatus || 'NEW')
                );
                const delivery_time = resolveOnlineDeliveryTimeForUpdate(
                    orderRow,
                    '',
                    'jreats',
                    jreatsMealDeliveryTimes
                );
                const agentRowIdNum = Number(getDeliveryAgentRecordId(agent));
                const body = {
                    order_status_code,
                    delivery_contact_name: getDeliveryAgentDisplayName(agent),
                    delivery_contact_phone: String(getDeliveryAgentPhone(agent) ?? ''),
                    delivery_contact_id: agentRowIdNum,
                    delivery_time,
                    updated_by_id: updatedById,
                };
                await axiosRestaurantInstance.put(`/restaurant/orders/${orderId}`, body, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const chosenName = getDeliveryAgentDisplayName(agent);
                updatedAssigned[orderId] = chosenName;
                orderUpdates.push({ orderId, agent, chosenName, delivery_time });
            }

            setAssignedAgents(updatedAssigned);
            setOrders((prev) =>
                prev.map((order) => {
                    const oid = resolveOrderId(order);
                    const upd = orderUpdates.find(
                        (u) => u.orderId === oid || String(u.orderId) === String(oid)
                    );
                    if (!upd) return order;
                    const agentIdPersist = getDeliveryAgentRecordId(upd.agent);
                    return {
                        ...order,
                        deliveryContactName: upd.chosenName,
                        deliveryContactNo: getDeliveryAgentPhone(upd.agent),
                        ...(agentIdPersist
                            ? {
                                  deliveryContactId: Number(agentIdPersist),
                                  delivery_contact_id: Number(agentIdPersist),
                              }
                            : {}),
                        deliveryTime: upd.delivery_time,
                        DeliveryTime: upd.delivery_time,
                    };
                })
            );

            handleCloseAssignAgentsPopup();
            window.alert(
                `Successfully assigned ${schoolOrders.length} order(s) to ${getDeliveryAgentDisplayName(agent)}.`
            );
        } catch (error) {
            console.error(
                'Error bulk assigning delivery agents:',
                error.response?.data || error.message
            );
            window.alert(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    'Could not assign delivery agents. Please try again.'
            );
        } finally {
            setAssignAgentsSaving(false);
        }
    };

    const deliveryAgentSearchNorm = deliveryAgentSearch.toLowerCase().trim();
    const filteredDeliveryAgentsForModal = deliveryAgents.filter((agent) => {
        const rid = getDeliveryAgentRecordId(agent);
        if (!rid) return false;
        if (!deliveryAgentSearchNorm) return true;
        const name = String(getDeliveryAgentDisplayName(agent) || '').toLowerCase();
        const phone = String(getDeliveryAgentPhone(agent) || '').toLowerCase();
        return name.includes(deliveryAgentSearchNorm) || phone.includes(deliveryAgentSearchNorm);
    });
    const modalSelectedDeliveryAgent = deliveryAgents.find(
        (a) => getDeliveryAgentRecordId(a) === selectedDeliveryAgentId
    );
    const modalDeliveryAgentSummaryLabel = modalSelectedDeliveryAgent
        ? `${getDeliveryAgentDisplayName(modalSelectedDeliveryAgent)}${
              getDeliveryAgentPhone(modalSelectedDeliveryAgent)
                  ? ` — ${getDeliveryAgentPhone(modalSelectedDeliveryAgent)}`
                  : ''
          }`
        : '— Select delivery agent —';
    const modalOrderRow =
        currentOrderId != null
            ? orders.find(
                  (o) =>
                      o.orderId === currentOrderId ||
                      String(o.orderId) === String(currentOrderId)
              )
            : null;
    const showDeliveryAgentInModal =
        !isMyEatsOrdersView(orderTypeFilter) || !isPickupOnlineOrder(modalOrderRow);
    const showMyEatsPickupDeliveryAgentColumn = showMyEatsDeliveryAgentColumn(
        orderTypeFilter,
        effectiveOnlineOrderTypeFilter
    );
    const modalStatusUpdateOptions = isMyEatsOrdersView(orderTypeFilter)
        ? getMyEatsStatusUpdateOptionsForOrder(effectiveOnlineOrderTypeFilter, modalOrderRow)
        : getOnlineStatusUpdateOptions(orderTypeFilter, effectiveOnlineOrderTypeFilter);

    // Filter orders by status and order type (offline/online/catering)
    const filteredOrders = orders.filter(order => {
        if (isOnlineOrdersView(orderTypeFilter)) {
            if (onlineOrderType === 'orders' && !isOrderCreatedToday(order)) {
                return false;
            }
        } else if (orderTypeFilter === 'offline' && isDineInOfflineOrdersView(offlineOrderType)) {
            if (!isOrderCreatedToday(order)) {
                return false;
            }
        }
        // Catering active orders are event-based — do not restrict to created-today.

        const hasTableNumber = orderHasTableNumber(order);
        const isDineIn = isDineInPosOrder(order);
        const isOffline = isDineIn;
        const isOnline = !isDineIn;
        
        // STRICT SEPARATION: If viewing online orders, immediately exclude any order with table number
        if (isOnlineOrdersView(orderTypeFilter) && isDineIn) {
            console.log('FilteredOrders: Excluding dine-in order from online:', order.orderId);
            return false;
        }
        
        // Check multiple possible property names for order status
        const orderStatus = (order.orderStatus || order.OrderStatus || order.order_status || 'NEW').toString().toUpperCase();
        const isSavedOrder = orderStatus === 'NEW';
        const statusNormOffline = normalizeOnlineOrderStatusForFilter(orderStatus);
        const isTerminalOffline = isDineInHistoryOrderStatus(statusNormOffline);
        
        // Dine-in tab: include DINE-IN orders (and legacy table-number / NEW / history rows)
        if (orderTypeFilter === 'offline' && !isDineIn && !isSavedOrder && !isTerminalOffline) {
            return false;
        }
        
        let isActive;
        if (orderTypeFilter === 'offline') {
            if (isDineInOfflineHistoryView(offlineOrderType)) {
                isActive = isTerminalOffline;
            } else {
                isActive = !isTerminalOffline && orderStatus !== 'CANCELLED';
            }
        } else if (isOnlineOrdersView(orderTypeFilter) && onlineOrderType === 'orderHistory') {
            const statusNormLocal = normalizeOnlineOrderStatusForFilter(
                order.orderStatus || order.OrderStatus || order.order_status || 'NEW'
            );
            const allowedHist = allowedOrderHistoryStatusesForType(
                effectiveOnlineOrderTypeFilter,
                orderTypeFilter
            );
            const isTerminal = allowedHist.includes(statusNormLocal);
            const isPastUndelivered = orderMatchesUndeliveredHistoryFilter(order);
            if (orderTypeFilter === 'jreats' && isJreatsTodayTerminalOrder(order)) {
                isActive = false;
            } else {
                isActive = isTerminal || isPastUndelivered;
            }
        } else if (orderTypeFilter === 'catering') {
            const normCatering = normalizeCateringOrderStatusNorm(order);
            if (isCateringHistoryView(cateringOrderType)) {
                isActive = isCateringHistoryOrderStatus(normCatering);
            } else {
                isActive = !isCateringHistoryOrderStatus(normCatering) && orderStatus !== 'CANCELLED';
            }
        } else if (orderTypeFilter === 'jreats') {
            isActive = true;
        } else {
            // Current orders: exclude CANCELLED orders
            isActive = orderStatus !== 'CANCELLED';
        }
        
        // Check if order is catering
        const isCatering = order.orderType === 'catering' || order.eventDate || order.eventLocation || order.numberOfGuests;
        
        // Apply order type filter
        if (orderTypeFilter === 'catering') {
            return isActive && isCatering;
        } else if (orderTypeFilter === 'offline') {
            return isActive && (isDineIn || isSavedOrder || isTerminalOffline) && !isCatering
                && isDineInMbOrder(order, { isDineInPosOrder });
        } else {
            // For online orders
            if (onlineOrderType === 'orderHistory') {
                return isActive && isOnline && !isCatering;
            }
            if (orderTypeFilter === 'jreats') {
                return isActive && isOnline && !isCatering;
            }
            // Current orders: exclude terminal statuses (delivered, picked up, cancelled, etc.)
            const isValidStatus = isMyEatsActiveOrderStatus(orderStatus);
            return isActive && isOnline && isValidStatus && !isCatering;
        }
    });

    const isDineInHistoryView = isDineInOfflineHistoryView(offlineOrderType);

    const filteredDineInOrders = (() => {
        if (orderTypeFilter !== 'offline') return [];
        let list = filteredOrders;
        const searchRaw = (isDineInHistoryView ? searchOrderIdDelivered : searchOrderIdNew).trim();
        if (searchRaw !== '') {
            list = list.filter((order) =>
                orderMatchesTableKeyword(order, searchRaw, 'dineIn')
            );
        }
        if (isDineInHistoryView) {
            list = list.filter((order) =>
                dineInOrderPassesHistoryStatusFilter(order, dineInHistoryStatusSelections)
            );
            list = list.filter((order) =>
                orderMatchesHistoryDateRange(order, historyFromDate, historyToDate)
            );
        } else {
            list = list.filter((order) =>
                dineInOrderPassesActiveStatusFilter(order, dineInActiveStatusSelections)
            );
        }
        return sortOrderList(list, dineInSort, 'dineIn');
    })();

    const filteredCateringOrders = (() => {
        if (orderTypeFilter !== 'catering') return [];
        let list = filteredOrders;
        if (searchOrderIdCatering.trim() !== '') {
            list = list.filter((order) =>
                orderMatchesTableKeyword(order, searchOrderIdCatering, 'catering')
            );
        }
        if (isCateringHistoryViewActive) {
            list = list.filter((order) =>
                cateringOrderPassesHistoryStatusFilter(order, cateringHistoryStatusSelections)
            );
        } else {
            list = list.filter((order) =>
                cateringOrderPassesActiveStatusFilter(order, cateringActiveStatusSelections)
            );
        }
        list = list.filter((order) =>
            orderMatchesHistoryDateRange(order, historyFromDate, historyToDate)
        );
        return sortOrderList(list, cateringSort, 'catering');
    })();
    
    const currentDineInPage = isDineInHistoryView ? currentPageDelivered : currentPageNew;
    const setCurrentDineInPage = isDineInHistoryView ? setCurrentPageDelivered : setCurrentPageNew;
    const indexOfLastDineIn = currentDineInPage * ordersPageSize;
    const indexOfFirstDineIn = indexOfLastDineIn - ordersPageSize;
    const currentDineInOrders = filteredDineInOrders.slice(indexOfFirstDineIn, indexOfLastDineIn);
    const totalPagesDineIn = Math.ceil(filteredDineInOrders.length / ordersPageSize);

    // Pagination calculations for Catering Orders
    const indexOfLastCatering = currentPageCatering * ordersPageSize;
    const indexOfFirstCatering = indexOfLastCatering - ordersPageSize;
    const currentCateringOrders = filteredCateringOrders.slice(indexOfFirstCatering, indexOfLastCatering);
    const totalPagesCatering = Math.ceil(filteredCateringOrders.length / ordersPageSize);

    const jreatsSchoolOptions = useMemo(() => {
        if (orderTypeFilter !== 'jreats') return [];
        const names = new Set();
        for (const order of filteredOrders) {
            const n = String(order.organizationName ?? order.OrganizationName ?? '').trim();
            if (n) names.add(n);
        }
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [filteredOrders, orderTypeFilter]);

    const assignAgentsSchoolSelectOptions = useMemo(
        () => jreatsSchoolOptions.map((name) => ({ value: name, label: name })),
        [jreatsSchoolOptions]
    );

    const assignAgentsDeliverySelectOptions = useMemo(
        () =>
            deliveryAgents
                .map((a) => {
                    const rid = getDeliveryAgentRecordId(a);
                    if (!rid) return null;
                    const phone = formatMobileDisplayOrFallback(getDeliveryAgentPhone(a), '');
                    const name = getDeliveryAgentDisplayName(a);
                    return {
                        value: rid,
                        label: phone ? `${name} — ${phone}` : name,
                    };
                })
                .filter(Boolean),
        [deliveryAgents]
    );

    useEffect(() => {
        if (orderTypeFilter !== 'jreats') return;
        setJreatsSchoolSelections((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const name of jreatsSchoolOptions) {
                if (!(name in next)) {
                    next[name] = false;
                    changed = true;
                }
            }
            for (const key of Object.keys(next)) {
                if (!jreatsSchoolOptions.includes(key)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [jreatsSchoolOptions, orderTypeFilter]);

    const onlineTableExtras = useMemo(
        () => ({
            formatJreatsMealType,
            getJreatsChildSchoolDisplay,
            assignedAgents,
        }),
        [assignedAgents]
    );

    // Filter online orders by table keyword search, then by status checkboxes on Orders tab
    const filteredOnlineOrders = (() => {
        if (!isOnlineOrdersView(orderTypeFilter)) return filteredOrders;
        const tableKind = orderTypeFilter === 'jreats' ? 'jreats' : 'online';
        let list = filteredOrders;
        if (searchOrderIdOnline.trim() !== '') {
            list = list.filter((order) =>
                orderMatchesTableKeyword(order, searchOrderIdOnline, tableKind, onlineTableExtras)
            );
        }
        if (orderTypeFilter === 'jreats') {
            if (jreatsMealFilter) {
                list = list.filter((order) => orderMatchesJreatsMealFilter(order, jreatsMealFilter));
            }
            const selectedSchools = jreatsSchoolOptions.filter((name) => jreatsSchoolSelections[name]);
            if (selectedSchools.length > 0) {
                const schoolSet = new Set(selectedSchools);
                list = list.filter((order) => {
                    const orgName = String(
                        order.organizationName ?? order.OrganizationName ?? ''
                    ).trim();
                    return schoolSet.has(orgName);
                });
            }
            if (onlineOrderType !== 'orderHistory') {
                list = list.filter((order) =>
                    jreatsOrderPassesStatusFilter(order, jreatsStatusSelections, JREATS_ORDER_STATUS_OPTS)
                );
            } else {
                list = list.filter((order) =>
                    jreatsOrderPassesStatusFilter(
                        order,
                        jreatsHistoryStatusSelections,
                        JREATS_ORDER_HISTORY_STATUS_OPTS
                    )
                );
            }
        } else if (onlineOrderType !== 'orderHistory') {
            list = list.filter((order) =>
                onlineOrderPassesStatusCheckboxFilter(
                    order,
                    onlineOrderStatusSelections,
                    effectiveOnlineOrderTypeFilter,
                    orderTypeFilter
                )
            );
        } else {
            list = list.filter((order) =>
                onlineOrderHistoryPassesStatusCheckboxFilter(
                    order,
                    onlineOrderHistoryStatusSelections,
                    effectiveOnlineOrderTypeFilter,
                    orderTypeFilter
                )
            );
        }
        if (onlineOrderType === 'orderHistory') {
            list = list.filter((order) =>
                orderMatchesHistoryDateRange(order, historyFromDate, historyToDate)
            );
        }
        if (
            orderTypeFilter !== 'jreats' &&
            showOrganizationColumnForOrders(orderTypeFilter) &&
            searchOrganizationOnline.trim() !== ''
        ) {
            const orgSearch = searchOrganizationOnline.trim().toLowerCase();
            list = list.filter((order) => {
                const orgName = String(
                    order.organizationName ?? order.OrganizationName ?? ''
                )
                    .trim()
                    .toLowerCase();
                return orgName.includes(orgSearch);
            });
        }
        return sortOrderList(list, onlineSort, tableKind, onlineTableExtras);
    })();

    // Pagination calculations for Online Orders (when isOnlineOrdersView(orderTypeFilter))
    const indexOfLastOnline = currentPageOnline * ordersPageSize;
    const indexOfFirstOnline = indexOfLastOnline - ordersPageSize;
    const currentOnlineOrders = filteredOnlineOrders.slice(indexOfFirstOnline, indexOfLastOnline);
    const totalPagesOnline = Math.ceil(filteredOnlineOrders.length / ordersPageSize);
    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPageNew(1);
        setCurrentPageDelivered(1);
        setCurrentPageCatering(1);
        setCurrentPageOnline(1);
        setSearchOrganizationOnline('');
        setJreatsMealFilter('');
        setJreatsSchoolSelections({});
        setDineInSort(null);
        setCateringSort(null);
        setOnlineSort(null);
        setJreatsStatusSelections(buildJreatsOrderStatusSelectionsDefault());
        setJreatsHistoryStatusSelections(buildJreatsHistoryStatusSelectionsDefault());
        if (orderTypeFilter === 'jreats' && setOnlineOrderType) {
            setOnlineOrderType('orders');
        }
    }, [orderTypeFilter, setOnlineOrderType]);

    useEffect(() => {
        setCurrentPageNew(1);
        setCurrentPageDelivered(1);
    }, [offlineOrderType]);

    useEffect(() => {
        setCurrentPageNew(1);
    }, [searchOrderIdNew, dineInActiveStatusSelections]);

    useEffect(() => {
        setCurrentPageDelivered(1);
    }, [searchOrderIdDelivered, dineInHistoryStatusSelections, historyFromDate, historyToDate]);

    useEffect(() => {
        setCurrentPageCatering(1);
    }, [cateringOrderType]);

    useEffect(() => {
        setCurrentPageCatering(1);
    }, [searchOrderIdCatering, cateringActiveStatusSelections, cateringHistoryStatusSelections, historyFromDate, historyToDate]);

    useEffect(() => {
        setDineInSort(null);
    }, [offlineOrderType]);

    useEffect(() => {
        setCateringSort(null);
    }, [cateringOrderType]);

    useEffect(() => {
        setOnlineSort(null);
    }, [onlineOrderType, orderTypeFilter]);

    // Reset online pagination when switching between Orders and Order History
    useEffect(() => {
        setCurrentPageOnline(1);
    }, [onlineOrderType]);

    // Reset online pagination when search or status checkboxes change
    useEffect(() => {
        setCurrentPageOnline(1);
    }, [
        searchOrderIdOnline,
        searchOrganizationOnline,
        onlineOrderStatusSelections,
        onlineOrderHistoryStatusSelections,
        effectiveOnlineOrderTypeFilter,
        jreatsMealFilter,
        jreatsSchoolSelections,
        jreatsStatusSelections,
        jreatsHistoryStatusSelections,
        historyFromDate,
        historyToDate,
    ]);

    // Pagination handlers
    const handlePageChangeNew = (pageNumber) => {
        setCurrentPageNew(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageChangeDelivered = (pageNumber) => {
        setCurrentPageDelivered(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageChangeCatering = (pageNumber) => {
        setCurrentPageCatering(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageChangeOnline = (pageNumber) => {
        setCurrentPageOnline(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleOrdersPageSizeChange = (nextSize) => {
        setOrdersPageSize(nextSize);
        setCurrentPageNew(1);
        setCurrentPageDelivered(1);
        setCurrentPageCatering(1);
        setCurrentPageOnline(1);
    };

    // Debug: Log orders to help troubleshoot
    console.log('Total orders:', orders.length);
    console.log('All orders:', orders);
    console.log('Filtered orders:', filteredOrders.length);
    console.log('Filtered orders details:', filteredOrders);
    console.log('Dine-in orders:', filteredDineInOrders.length);
    console.log('Current filter:', orderTypeFilter);

    const onlineOrderStatusFilterOpts = getOnlineOrderStatusCheckboxOptionsForType(
        effectiveOnlineOrderTypeFilter,
        orderTypeFilter
    );
    const onlineHistoryStatusFilterOpts = getOrderHistoryStatusCheckboxOptions(
        effectiveOnlineOrderTypeFilter,
        orderTypeFilter
    );
    const onlineStatusUpdateOptions = getOnlineStatusUpdateOptions(
        orderTypeFilter,
        effectiveOnlineOrderTypeFilter
    );
    const dineInStatusUpdateOptions = DINE_IN_STATUS_UPDATE_OPTIONS;
    const cateringStatusUpdateOptions = CATERING_STATUS_UPDATE_OPTIONS;
    const activeOnlineRestaurantId = resolveRestaurantForFetch();

    const jreatsActiveStatusOpts =
        onlineOrderType === 'orderHistory'
            ? JREATS_ORDER_HISTORY_STATUS_OPTS
            : JREATS_ORDER_STATUS_OPTS;
    const jreatsActiveStatusSelections =
        onlineOrderType === 'orderHistory'
            ? jreatsHistoryStatusSelections
            : jreatsStatusSelections;
    const setJreatsActiveStatusSelections =
        onlineOrderType === 'orderHistory'
            ? setJreatsHistoryStatusSelections
            : setJreatsStatusSelections;

    const toggleJreatsSchool = (name) => {
        setJreatsSchoolSelections((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const toggleJreatsStatus = (code) => {
        setJreatsActiveStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const setAllJreatsStatuses = (checked) => {
        setJreatsActiveStatusSelections((prev) => {
            const next = { ...prev };
            jreatsActiveStatusOpts.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    return (
        <div className="menu orders-page">
            {isOnlineOrdersView(orderTypeFilter) && (
                <>
            <div
                className={`today-order-toolbar today-order-toolbar--online${
                    orderTypeFilter === 'jreats' ? ' today-order-toolbar--jreats' : ''
                }`}
            >
                <div className="today-order-toolbar__row">
                    <div className="today-order-toolbar__main">
                        {serviceChannelSelect}
                        <CollapsibleFilters>
                        {setOnlineOrderType && (
                            <ToolbarRichSelect
                                panelId="online-view-filter"
                                value={onlineOrderType || 'orders'}
                                onChange={(v) => {
                                    const view = v === 'orderHistory' ? 'orderHistory' : 'orders';
                                    setOnlineOrderType(view);
                                    const next = new URLSearchParams(window.location.search);
                                    next.set('tab', 'orders');
                                    next.set(
                                        'orderType',
                                        orderTypeFilter === 'jreats' ? 'jreats' : 'myeats'
                                    );
                                    if (view === 'orderHistory') {
                                        next.set('onlineView', 'orderHistory');
                                    } else {
                                        next.delete('onlineView');
                                    }
                                    navigate({ search: `?${next.toString()}` });
                                }}
                                options={ORDER_VIEW_OPTIONS}
                                ariaLabel="Active orders or order history"
                                panelTitle="Order view"
                            />
                        )}
                        {orderTypeFilter !== 'jreats' && (
                            <ToolbarRichSelect
                                panelId="online-channel-filter"
                                value={onlineOrderTypeFilter}
                                onChange={(v) => setOnlineOrderTypeFilter(v)}
                                options={ONLINE_CHANNEL_OPTIONS}
                                ariaLabel="Delivery or pickup"
                                panelTitle="Order channel"
                                narrow
                            />
                        )}
                        {orderTypeFilter !== 'jreats' &&
                            showOrganizationColumnForOrders(orderTypeFilter) && (
                        <input
                            type="text"
                            placeholder="Search by organization"
                            value={searchOrganizationOnline}
                            onChange={(e) => setSearchOrganizationOnline(e.target.value)}
                            className="today-order-org-search"
                            aria-label="Search orders by organization"
                            autoComplete="off"
                        />
                        )}
                        {orderTypeFilter !== 'jreats' && (!onlineOrderType || onlineOrderType === 'orders') ? (
                            <OrderStatusFilterDropdown
                                panelId="online-order-status-filter"
                                statusOptions={onlineOrderStatusFilterOpts}
                                statusSelections={onlineOrderStatusSelections}
                                onToggleStatus={toggleOnlineOrderStatusFilter}
                                onSetAllStatuses={setAllOnlineOrderStatuses}
                            />
                        ) : null}
                        {orderTypeFilter !== 'jreats' && onlineOrderType === 'orderHistory' ? (
                            <OrderStatusFilterDropdown
                                panelId="online-order-history-status-filter"
                                statusOptions={onlineHistoryStatusFilterOpts}
                                statusSelections={onlineOrderHistoryStatusSelections}
                                onToggleStatus={toggleOnlineOrderHistoryStatusFilter}
                                onSetAllStatuses={setAllOnlineOrderHistoryStatuses}
                            />
                        ) : null}
                        {orderTypeFilter !== 'jreats' && onlineOrderType === 'orderHistory' ? (
                            <OrderHistoryDateFilters
                                fromDate={historyFromDate}
                                toDate={historyToDate}
                                onFromDateChange={setHistoryFromDate}
                                onToDateChange={setHistoryToDate}
                            />
                        ) : null}
                        {orderTypeFilter === 'jreats' ? (
                            <JreatsOrderFilters
                                mealFilter={jreatsMealFilter}
                                onMealFilterChange={setJreatsMealFilter}
                                schoolOptions={jreatsSchoolOptions}
                                schoolSelections={jreatsSchoolSelections}
                                onToggleSchool={toggleJreatsSchool}
                                statusOptions={jreatsActiveStatusOpts}
                                statusSelections={jreatsActiveStatusSelections}
                                onToggleStatus={toggleJreatsStatus}
                                onSetAllStatuses={setAllJreatsStatuses}
                                showStatusSelectAll
                            />
                        ) : null}
                        {orderTypeFilter === 'jreats' && onlineOrderType === 'orderHistory' ? (
                            <OrderHistoryDateFilters
                                fromDate={historyFromDate}
                                toDate={historyToDate}
                                onFromDateChange={setHistoryFromDate}
                                onToDateChange={setHistoryToDate}
                            />
                        ) : null}
                        </CollapsibleFilters>
                        {orderTypeFilter !== 'jreats' ? (
                            <button
                                type="button"
                                className="menu-toolbar-btn today-order-toolbar__refresh"
                                onClick={handleRefreshOrders}
                                disabled={isLoadingOnlineOrders}
                                aria-busy={isLoadingOnlineOrders}
                                aria-label="Refresh orders"
                                title="Refresh"
                            >
                                <FaSyncAlt
                                    className={isLoadingOnlineOrders ? 'is-spinning' : ''}
                                    aria-hidden="true"
                                />
                            </button>
                        ) : null}
                        {orderTypeFilter === 'jreats' ? (
                            <div className="today-order-jreats-actions">
                                {onlineOrderType !== 'orderHistory' ? (
                                    <button
                                        type="button"
                                        className="add-button"
                                        onClick={handleOpenAssignAgentsModal}
                                        disabled={isLoadingOnlineOrders || assignAgentsSaving}
                                    >
                                        Assign Agents
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    className="menu-toolbar-btn today-order-refresh-button--jreats-end today-order-toolbar__refresh"
                                    onClick={handleRefreshOrders}
                                    disabled={isLoadingOnlineOrders}
                                    aria-busy={isLoadingOnlineOrders}
                                    aria-label="Refresh orders"
                                    title="Refresh"
                                >
                                    <FaSyncAlt
                                        className={isLoadingOnlineOrders ? 'is-spinning' : ''}
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
                </>
            )}
    
            {/* Dine-in Orders Section */}
            {orderTypeFilter === 'offline' && (
                <>
                    <div className="today-order-toolbar today-order-toolbar--dine-in">
                        <div className="today-order-toolbar__row">
                            <div className="today-order-toolbar__main">
                                {serviceChannelSelect}
                                <CollapsibleFilters>
                                <ToolbarRichSelect
                                    panelId="dine-in-view-filter"
                                    value={normalizeOfflineOrderView(offlineOrderType)}
                                    onChange={(v) => {
                                        const view = normalizeOfflineOrderView(v);
                                        setOfflineOrderType(view);
                                        const next = new URLSearchParams(window.location.search);
                                        next.set('tab', 'orders');
                                        next.set('orderType', 'offline');
                                        if (view === 'orderHistory') {
                                            next.set('offlineView', 'orderHistory');
                                        } else {
                                            next.delete('offlineView');
                                        }
                                        navigate({ search: `?${next.toString()}` });
                                    }}
                                    options={ORDER_VIEW_OPTIONS}
                                    ariaLabel="Active orders or order history"
                                    panelTitle="Order view"
                                />
                                {!isDineInHistoryView ? (
                                    <OrderStatusFilterDropdown
                                        panelId="dine-in-order-status-filter"
                                        statusOptions={DINE_IN_ACTIVE_STATUS_OPTS}
                                        statusSelections={dineInActiveStatusSelections}
                                        onToggleStatus={toggleDineInActiveStatusFilter}
                                        onSetAllStatuses={setAllDineInActiveStatuses}
                                    />
                                ) : (
                                    <OrderStatusFilterDropdown
                                        panelId="dine-in-order-history-status-filter"
                                        statusOptions={DINE_IN_HISTORY_STATUS_OPTS}
                                        statusSelections={dineInHistoryStatusSelections}
                                        onToggleStatus={toggleDineInHistoryStatusFilter}
                                        onSetAllStatuses={setAllDineInHistoryStatuses}
                                    />
                                )}
                                {isDineInHistoryView ? (
                                    <OrderHistoryDateFilters
                                        fromDate={historyFromDate}
                                        toDate={historyToDate}
                                        onFromDateChange={setHistoryFromDate}
                                        onToDateChange={setHistoryToDate}
                                    />
                                ) : null}
                                </CollapsibleFilters>
                                <button
                                    type="button"
                                    className="menu-toolbar-btn today-order-toolbar__refresh"
                                    onClick={handleRefreshOrders}
                                    aria-label="Refresh orders"
                                    title="Refresh"
                                >
                                    <FaSyncAlt aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <OrdersTablePanel
                        searchValue={isDineInHistoryView ? searchOrderIdDelivered : searchOrderIdNew}
                        onSearchChange={(v) =>
                            isDineInHistoryView
                                ? setSearchOrderIdDelivered(v)
                                : setSearchOrderIdNew(v)
                        }
                        resultCount={filteredDineInOrders.length}
                        className="today-order-table-container--dine-in"
                        tableWrapperClassName="order-history-table-container dine-in-orders-table-container"
                    >
                        <div className="dine-in-orders-table-inner">
                        <table
                            className={`menu-table order-history-table offline-orders-table ${
                                isDineInHistoryView
                                    ? 'dine-in-orders-table--9-cols'
                                    : 'dine-in-orders-table--10-cols'
                            }`}
                        >
                            <thead>
                                <tr>
                                    <OrdersSortableTh label="Order#" sortKey="orderId" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Table#" sortKey="tableNumber" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Food Items" sortKey="foodItems" className="dine-in-col-food-items" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Total (₹)" sortKey="price" className="dine-in-col-total" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Order Status" sortKey="orderStatus" className="dine-in-col-order-status" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Pay Status" sortKey="paymentStatus" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Method" sortKey="paymentMethod" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Mobile#" sortKey="mobile" className="table-col-mobile" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={dineInSort?.key} sortDirection={dineInSort?.direction} onSortAsc={handleDineInSortAsc} onSortDesc={handleDineInSortDesc} />
                                    {!isDineInHistoryView && <th className="menu-table-actions-head">Action</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {currentDineInOrders.length > 0 ? (
                                    currentDineInOrders.map((order) => {
                                        const foodItems = order.orderLineItems || order.OrderLineItems || [];
                                        const statusNorm = normalizeDineInOrderStatusNorm(order);
                                        const isTerminalRow = isDineInHistoryOrderStatus(statusNorm);
                                        const displayStatus =
                                            statusNorm === 'SRV' ||
                                            statusNorm === 'SERVED' ||
                                            statusNorm === 'SERVING'
                                                ? 'SERVED'
                                                : order.orderStatus || order.OrderStatus || 'NEW';
                                        const dineInNextStep =
                                            allowOrderStatusUpdate &&
                                            !isDineInHistoryView &&
                                            !isTerminalRow
                                                ? getNextOrderStep('offline', displayStatus)
                                                : null;
                                        return (
                                            <React.Fragment key={order.orderId}>
                                                <tr>
                                                    <td>
                                                        <span
                                                            className="order-id-link"
                                                            onClick={() => handleOrderIdClick(order.orderId)}
                                                        >
                                                            {formatDisplayOrderNo(order)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span>
                                                            {formatTableNumberDisplay(
                                                                order.tableNumber ??
                                                                    order.table_number ??
                                                                    order.TableNumber ??
                                                                    order.table_no ??
                                                                    order.tableNo
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td className="dine-in-col-food-items">
                                                        <OrderItemsSummary
                                                            items={foodItems}
                                                            onClick={() => handleFoodItemsClick(order)}
                                                            emptyText="No food items"
                                                        />
                                                    </td>
                                                    <td className="dine-in-col-total">
                                                        {parseFloat(order.totalPrice || 0).toFixed(2)}
                                                    </td>
                                                    <td className="dine-in-col-order-status">
                                                        <OrderStatusPill
                                                            status={displayStatus}
                                                            onClick={
                                                                allowOrderStatusUpdate &&
                                                                !isDineInHistoryView
                                                                    ? () =>
                                                                          handleOpenStatusUpdateModal(
                                                                              order.orderId,
                                                                              order.orderStatus ?? order.OrderStatus
                                                                          )
                                                                    : undefined
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <span>{(order.paymentStatus || '').toString().toUpperCase() || 'N/A'}</span>
                                                    </td>
                                                    <td>{formatPaymentMethodDisplay(order)}</td>
                                                    <td className="table-col-mobile">{formatOrderMobileDisplay(order)}</td>
                                                    <td>{formatOrderDateTime(order.createdDate || order.createdAt)}</td>
                                                    {!isDineInHistoryView && (
                                                        <td className="action-cell menu-table-actions-cell">
                                                            {dineInNextStep && (
                                                                <button
                                                                    type="button"
                                                                    className="order-next-step-btn"
                                                                    title={dineInNextStep.actionLabel}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleStatusChange(
                                                                            order.orderId,
                                                                            dineInNextStep.nextStatus
                                                                        );
                                                                    }}
                                                                >
                                                                    {dineInNextStep.actionLabel}
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="action-button"
                                                                title="Edit order"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleActionClick(order, 'edit');
                                                                }}
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            {!isTerminalRow && (
                                                                <button
                                                                    type="button"
                                                                    className="order-cancel-btn"
                                                                    title="Cancel order"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleActionClick(order.orderId, 'cancel');
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                                {selectedOrderItems === order.orderId && foodItems.length > 0 && (
                                                    <tr>
                                                        <td colSpan={isDineInHistoryView ? 9 : 10}>
                                                            <ul className="today-order-food-list">
                                                                {foodItems.map((item, index) => (
                                                                    <li key={index}>
                                                                        {item.foodItemName || item.Food_Item_Name || item.itemName || 'Item'} – Quantity: {item.foodQuantity ?? item.Food_Quantity ?? 1}, Price: ₹{parseFloat(item.price ?? item.Price ?? 0).toFixed(2)}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={isDineInHistoryView ? 9 : 10} className="no-orders-message menu-table-empty-cell">
                                            {isDineInHistoryView ? 'No order history' : 'No orders found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                    </OrdersTablePanel>
                    <OrdersTablePagination
                        currentPage={currentDineInPage}
                        totalPages={totalPagesDineIn}
                        totalItems={filteredDineInOrders.length}
                        pageSize={ordersPageSize}
                        onPageChange={(page) => {
                            setCurrentDineInPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        onPageSizeChange={handleOrdersPageSizeChange}
                    />
                </>
            )}

            {/* Party Hall Quotes / Orders (confirmed bookings) */}
            {orderTypeFilter === 'partyHall' && (
                <PartyHallPanel
                    restaurantId={resolveRestaurantForFetch()}
                    ordersPageSize={ordersPageSize}
                    onPageSizeChange={handleOrdersPageSizeChange}
                    activeSection={partyHallOrderType === 'orders' ? 'orders' : 'quotes'}
                    onSectionChange={handlePartyHallSectionChange}
                    serviceChannelSelect={serviceChannelSelect}
                />
            )}

            {/* Catering Orders / Quotes */}
            {orderTypeFilter === 'catering' && (
                <>
                    {isCateringQuotesView(cateringOrderType) ? (
                        <CateringQuotesPanel
                            restaurantId={resolveRestaurantForFetch()}
                            ordersPageSize={ordersPageSize}
                            onPageSizeChange={handleOrdersPageSizeChange}
                            onCateringSectionChange={handleCateringSectionChange}
                            serviceChannelSelect={serviceChannelSelect}
                        />
                    ) : (
                <>
                    <div className="today-order-toolbar today-order-toolbar--catering-orders">
                        <div className="today-order-toolbar__row">
                            <div className="today-order-toolbar__main">
                                {serviceChannelSelect}
                                <CateringViewSwitch
                                    activeSection="orders"
                                    onChange={handleCateringSectionChange}
                                />
                                <CollapsibleFilters>
                                <ToolbarRichSelect
                                    panelId="catering-view-filter"
                                    value={normalizeCateringOrderView(cateringOrderType)}
                                    onChange={handleCateringOrdersViewChange}
                                    options={ORDER_VIEW_OPTIONS}
                                    ariaLabel="Active catering orders or order history"
                                    panelTitle="Catering orders view"
                                />
                                {!isCateringHistoryViewActive ? (
                                    <OrderStatusFilterDropdown
                                        panelId="catering-order-status-filter"
                                        statusOptions={CATERING_ACTIVE_STATUS_OPTS}
                                        statusSelections={cateringActiveStatusSelections}
                                        onToggleStatus={toggleCateringActiveStatusFilter}
                                        onSetAllStatuses={setAllCateringActiveStatuses}
                                    />
                                ) : (
                                    <OrderStatusFilterDropdown
                                        panelId="catering-order-history-status-filter"
                                        statusOptions={CATERING_HISTORY_STATUS_OPTS}
                                        statusSelections={cateringHistoryStatusSelections}
                                        onToggleStatus={toggleCateringHistoryStatusFilter}
                                        onSetAllStatuses={setAllCateringHistoryStatuses}
                                    />
                                )}
                                <OrderHistoryDateFilters
                                    fromDate={historyFromDate}
                                    toDate={historyToDate}
                                    onFromDateChange={setHistoryFromDate}
                                    onToDateChange={setHistoryToDate}
                                    ariaLabel={
                                        isCateringHistoryViewActive
                                            ? 'Catering order history date range'
                                            : 'Catering orders date range'
                                    }
                                />
                                </CollapsibleFilters>
                                <button
                                    type="button"
                                    className="menu-toolbar-btn today-order-toolbar__refresh"
                                    onClick={handleRefreshOrders}
                                    aria-label="Refresh orders"
                                    title="Refresh"
                                >
                                    <FaSyncAlt aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>
                <OrdersTablePanel
                    searchValue={searchOrderIdCatering}
                    onSearchChange={setSearchOrderIdCatering}
                    resultCount={filteredCateringOrders.length}
                    tableWrapperClassName="today-order-table-container"
                >
                    <table className="menu-table today-order-table today-order-table--catering">
                        <thead>
                            <tr>
                                <OrdersSortableTh label="Order#" sortKey="orderId" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Customer Name" sortKey="customerName" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Mobile#" sortKey="whatsapp" className="table-col-mobile" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Alt Number" sortKey="altNumber" className="table-col-mobile" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Event Date" sortKey="eventDate" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Event Type" sortKey="eventType" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Event Location" sortKey="eventLocation" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Food Items" sortKey="foodItems" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Number of Guests" sortKey="numberOfGuests" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Quoted ₹" sortKey="totalPrice" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Advance Payment" sortKey="advancePayment" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Pending Amount" sortKey="pendingAmount" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Order Status" sortKey="orderStatus" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Payment Method" sortKey="paymentMethod" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={cateringSort?.key} sortDirection={cateringSort?.direction} onSortAsc={handleCateringSortAsc} onSortDesc={handleCateringSortDesc} />
                                {!isCateringHistoryViewActive && <th className="menu-table-actions-head">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {currentCateringOrders.length > 0 ? (
                                currentCateringOrders.map((order) => {
                                    // Format date
                                    const formatDate = (dateString) => {
                                        if (!dateString) return 'N/A';
                                        try {
                                            const date = new Date(dateString);
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const year = date.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        } catch (e) {
                                            return dateString;
                                        }
                                    };

                                    // Get food items (support orderLineItems and lineItems from API)
                                    const foodItems = order.orderLineItems || order.OrderLineItems || order.lineItems || [];
                                    const cateringNextStep =
                                        allowOrderStatusUpdate &&
                                        !isCateringHistoryViewActive &&
                                        !isCateringHistoryOrderStatus(normalizeCateringOrderStatusNorm(order))
                                            ? getNextOrderStep('catering', cateringDisplayStatusLabel(order))
                                            : null;

                                    return (
                                        <React.Fragment key={order.orderId}>
                                            <tr>
                                                <td>
                                                    <span 
                                                        className="order-id-link"
                                                        onClick={() => handleOrderIdClick(order.orderId)}
                                                    >
                                                        {formatDisplayOrderNo(order)}
                                                    </span>
                                                </td>
                                                <td>{order.customerName || order.CustomerName || 'N/A'}</td>
                                                <td className="table-col-mobile">{formatOrderMobileDisplay(order)}</td>
                                                <td className="table-col-mobile">{formatOrderAltMobileDisplay(order)}</td>
                                                <td>{order.eventDate ? formatDate(order.eventDate) : 'N/A'}</td>
                                                <td>{order.eventType || order.EventType || 'N/A'}</td>
                                                <td>{order.eventLocation || order.EventLocation || order.deliveryAddress || 'N/A'}</td>
                                                <td>
                                                    <OrderItemsSummary
                                                        items={foodItems}
                                                        onClick={() => handleFoodItemsClick(order)}
                                                        emptyText="No food items"
                                                    />
                                                </td>
                                                <td>{order.numberOfGuests ?? order.NumberOfGuests ?? order.number_of_guests ?? 'N/A'}</td>
                                                <td>{parseFloat(order.totalPrice || 0).toFixed(2)}</td>
                                                <td>
                                                    {(() => {
                                                        const advancePayment = order.advancePayment || order.advance_payment;
                                                        if (advancePayment) {
                                                            // Round to 2 decimal places to avoid floating point precision issues
                                                            const rounded = Math.round(parseFloat(advancePayment) * 100) / 100;
                                                            return `₹${rounded.toFixed(2)}`;
                                                        }
                                                        return 'N/A';
                                                    })()}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const advancePayment = order.advancePayment || order.advance_payment;
                                                        const pendingAmount = order.pendingAmount || order.pending_amount;
                                                        const totalPrice = order.totalPrice || 0;
                                                        
                                                        if (pendingAmount) {
                                                            // Round to 2 decimal places to avoid floating point precision issues
                                                            const rounded = Math.round(parseFloat(pendingAmount) * 100) / 100;
                                                            return `₹${rounded.toFixed(2)}`;
                                                        } else if (advancePayment) {
                                                            // Calculate pending amount if not stored
                                                            const totalRounded = Math.round(parseFloat(totalPrice) * 100) / 100;
                                                            const advanceRounded = Math.round(parseFloat(advancePayment) * 100) / 100;
                                                            const pending = Math.max(0, Math.round((totalRounded - advanceRounded) * 100) / 100);
                                                            return `₹${pending.toFixed(2)}`;
                                                        }
                                                        return 'N/A';
                                                    })()}
                                                </td>
                                                <td>
                                                    <OrderStatusPill
                                                        status={cateringDisplayStatusLabel(order)}
                                                        onClick={
                                                            allowOrderStatusUpdate &&
                                                            !isCateringHistoryViewActive
                                                                ? () =>
                                                                      handleOpenStatusUpdateModal(
                                                                          order.orderId,
                                                                          order.orderStatus ?? order.OrderStatus
                                                                      )
                                                                : undefined
                                                        }
                                                    />
                                                </td>
                                                <td>{formatPaymentMethodDisplay(order)}</td>
                                                <td>{formatOrderDateTime(order.createdDate || order.createdAt)}</td>
                                                {!isCateringHistoryViewActive && (
                                                    <td className="action-cell menu-table-actions-cell">
                                                        {cateringNextStep && (
                                                            <button
                                                                type="button"
                                                                className="order-next-step-btn"
                                                                title={cateringNextStep.actionLabel}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleStatusChange(
                                                                        order.orderId,
                                                                        cateringNextStep.nextStatus
                                                                    );
                                                                }}
                                                            >
                                                                {cateringNextStep.actionLabel}
                                                            </button>
                                                        )}
                                                        <button 
                                                            type="button"
                                                            className="action-button"
                                                            title="Edit"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleActionClick(order, 'edit');
                                                            }}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        {!isCateringHistoryOrderStatus(
                                                            normalizeCateringOrderStatusNorm(order)
                                                        ) && (
                                                            <button
                                                                type="button"
                                                                className="order-cancel-btn"
                                                                title="Cancel order"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleActionClick(order.orderId, 'cancel');
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                            {selectedOrderItems === order.orderId && foodItems.length > 0 && (
                                                <tr>
                                                    <td colSpan={isCateringHistoryViewActive ? 15 : 16}>
                                                        <ul className="today-order-food-list">
                                                            {foodItems.map((item, index) => (
                                                                <li key={index}>
                                                                    {item.foodItemName || item.FoodItemName || item.itemName} - Quantity: {item.foodQuantity || item.FoodQuantity || item.quantity}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={isCateringHistoryViewActive ? 15 : 16} className="no-orders-message menu-table-empty-cell">
                                        {isCateringHistoryViewActive ? 'No order history' : 'No catering orders'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </OrdersTablePanel>

                    <OrdersTablePagination
                        currentPage={currentPageCatering}
                        totalPages={totalPagesCatering}
                        totalItems={filteredCateringOrders.length}
                        pageSize={ordersPageSize}
                        onPageChange={handlePageChangeCatering}
                        onPageSizeChange={handleOrdersPageSizeChange}
                    />
                </>
                    )}
                </>
            )}

            {/* Online Orders Table */}
            {isOnlineOrdersView(orderTypeFilter) && (
                <>
                    {activeOnlineRestaurantId ? (
                        <>
                        <OrdersTablePanel
                            searchValue={searchOrderIdOnline}
                            onSearchChange={setSearchOrderIdOnline}
                            resultCount={filteredOnlineOrders.length}
                            tableWrapperClassName="today-order-table-container"
                        >
                            <table
                                className={`menu-table today-order-table${
                                    orderTypeFilter === 'jreats'
                                        ? ' today-order-table--jreats'
                                        : ' today-order-table--online'
                                }`}
                            >
                                <thead>
                                    <tr>
                                        {orderTypeFilter === 'jreats' ? (
                                            <>
                                                <OrdersSortableTh label="Order#" sortKey="orderId" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Meal Type" sortKey="mealType" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Food Items" sortKey="foodItems" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Total ₹" sortKey="price" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Billing" sortKey="billingType" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="School" sortKey="school" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Class" sortKey="class" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Section" sortKey="section" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Roll No" sortKey="rollNo" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Receiver" sortKey="receiverName" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Status" sortKey="status" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Delivery Agent" sortKey="assignedAgent" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                {onlineOrderType !== 'orderHistory' && (
                                                    <th className="menu-table-actions-head">Actions</th>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <OrdersSortableTh label="Order#" sortKey="orderId" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Customer Name" sortKey="customerName" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Food Items" sortKey="foodItems" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label={orderTypeFilter === 'myeats' ? 'Total ₹' : 'Total Price'} sortKey="totalPrice" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Order Status" sortKey="orderStatus" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                <OrdersSortableTh label="Delivery Time" sortKey="deliveryTime" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                {showOrganizationColumnForOrders(orderTypeFilter) && (
                                                    <OrdersSortableTh label="Organization Name" sortKey="organizationName" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                )}
                                                <OrdersSortableTh label="Receiver" sortKey="receiverName" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                {showMyEatsPickupDeliveryAgentColumn && (
                                                    <OrdersSortableTh label="Delivery Agent" sortKey="assignedAgent" activeSortKey={onlineSort?.key} sortDirection={onlineSort?.direction} onSortAsc={handleOnlineSortAsc} onSortDesc={handleOnlineSortDesc} />
                                                )}
                                                {onlineOrderType !== 'orderHistory' && (
                                                    <th className="menu-table-actions-head">Actions</th>
                                                )}
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                        {isLoadingOnlineOrders ? (
                            <tr>
                                <td
                                    colSpan={onlineOrdersTableColSpan(
                                        onlineOrderType,
                                        orderTypeFilter,
                                        effectiveOnlineOrderTypeFilter
                                    )}
                                    className="no-orders-message menu-table-empty-cell"
                                >
                                    Loading...
                                </td>
                            </tr>
                        ) : currentOnlineOrders.length > 0 ? (
                            currentOnlineOrders.map((order) => {
                                const orderId = resolveOrderId(order);
                                const lineItems = order.OrderLineItems || order.orderLineItems || [];
                                const childSchool =
                                    orderTypeFilter === 'jreats' ? getJreatsChildSchoolDisplay(order) : null;
                                const isOrderHistory = onlineOrderType === 'orderHistory';
                                const statusNorm = (order.orderStatus ?? order.OrderStatus ?? '')
                                    .toString()
                                    .trim()
                                    .toUpperCase()
                                    .replace(/[\s_-]+/g, '');
                                const isDeliveredOnly = statusNorm === 'DELIVERED';
                                const isCancelledOrder = isCancelledOrderStatus(order);
                                const onlineNextStep =
                                    allowOrderStatusUpdate && !isOrderHistory
                                        ? getNextOrderStep(
                                              orderTypeFilter,
                                              order.orderStatus ?? order.OrderStatus,
                                              order
                                          )
                                        : null;
                                const formatOrderDate = (dateString) => {
                                    if (!dateString) return 'N/A';
                                    try {
                                        const date = new Date(dateString);
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const year = date.getFullYear();
                                        return `${day}/${month}/${year}`;
                                    } catch (e) {
                                        return dateString;
                                    }
                                };
                                return (
                            <React.Fragment key={orderId || `order-${currentOnlineOrders.indexOf(order)}`}>
                                <tr>
                                    {orderTypeFilter === 'jreats' ? (
                                        <>
                                            <td>
                                                <span
                                                    className="order-id-link"
                                                    onClick={() => handleOrderIdClick(orderId)}
                                                >
                                                    {orderId}
                                                </span>
                                                {resolvePartySize(order) ? (
                                                    <span className="group-order-badge">
                                                        Group · {resolvePartySize(order)}
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td>{formatJreatsMealType(order)}</td>
                                            <td>
                                                <OrderItemsSummary
                                                    items={lineItems}
                                                    onClick={() => handleFoodItemsClick(order)}
                                                    emptyText="No food items"
                                                />
                                            </td>
                                            <td>
                                                {parseFloat(
                                                    order.totalPrice ?? order.TotalPrice ?? 0
                                                ).toFixed(2)}
                                            </td>
                                            <td>
                                                <OrderBillingCell order={order} />
                                            </td>
                                            <td>
                                                {order.organizationName ||
                                                    order.OrganizationName ||
                                                    'N/A'}
                                            </td>
                                            <td>{childSchool.className}</td>
                                            <td>{childSchool.section}</td>
                                            <td>{childSchool.rollNumber}</td>
                                            <td>
                                                {order.receiverName || order.ReceiverName || 'N/A'}
                                            </td>
                                            <td>
                                                <OrderStatusPill
                                                    status={order.orderStatus || order.OrderStatus}
                                                    onClick={
                                                        allowOrderStatusUpdate &&
                                                        !isOrderHistory &&
                                                        !isCancelledOrder
                                                            ? () =>
                                                                  handleOpenStatusUpdateModal(
                                                                      orderId,
                                                                      order.orderStatus ?? order.OrderStatus
                                                                  )
                                                            : undefined
                                                    }
                                                />
                                            </td>
                                            <td>
                                                {assignedAgents[orderId] ? (
                                                    <span className="assigned-agent-name">
                                                        {assignedAgents[orderId]}
                                                    </span>
                                                ) : allowOrderStatusUpdate &&
                                                  !isOrderHistory &&
                                                  !isCancelledOrder ? (
                                                    <button
                                                        type="button"
                                                        className="assign-agent-link"
                                                        onClick={() =>
                                                            handleOpenStatusUpdateModal(
                                                                orderId,
                                                                order.orderStatus ?? order.OrderStatus
                                                            )
                                                        }
                                                    >
                                                        Assign
                                                    </button>
                                                ) : (
                                                    isOrderHistory ? 'Not assigned' : '—'
                                                )}
                                            </td>
                                            <td>
                                                {formatOrderDate(
                                                    order.createdDate || order.CreatedDate
                                                )}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>
                                                <span
                                                    className="order-id-link"
                                                    onClick={() => handleOrderIdClick(orderId)}
                                                >
                                                    {orderId}
                                                </span>
                                                {resolvePartySize(order) ? (
                                                    <span className="group-order-badge">
                                                        Group · {resolvePartySize(order)}
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td>
                                                {order.customerName || order.CustomerName || 'N/A'}
                                            </td>
                                            <td>
                                                <OrderItemsSummary
                                                    items={lineItems}
                                                    onClick={() => handleFoodItemsClick(order)}
                                                    emptyText="No food items"
                                                />
                                            </td>
                                            <td>
                                                {parseFloat(
                                                    order.totalPrice ?? order.TotalPrice ?? 0
                                                ).toFixed(2)}
                                            </td>
                                            <td>
                                                <OrderStatusPill
                                                    status={order.orderStatus || order.OrderStatus}
                                                    onClick={
                                                        allowOrderStatusUpdate &&
                                                        !isOrderHistory &&
                                                        !isCancelledOrder
                                                            ? () =>
                                                                  handleOpenStatusUpdateModal(
                                                                      orderId,
                                                                      order.orderStatus ?? order.OrderStatus
                                                                  )
                                                            : undefined
                                                    }
                                                />
                                            </td>
                                            <td>
                                                {formatOrderDateTime(
                                                    order.createdDate || order.CreatedDate
                                                )}
                                            </td>
                                            <td>
                                                {formatOrderDateTime(
                                                    order.deliveryTime ??
                                                        order.DeliveryTime ??
                                                        order.Delivery_Time,
                                                    'N/A'
                                                )}
                                            </td>
                                            {showOrganizationColumnForOrders(orderTypeFilter) && (
                                                <td>
                                                    {order.organizationName ||
                                                        order.OrganizationName ||
                                                        'N/A'}
                                                </td>
                                            )}
                                            <td>
                                                {order.receiverName || order.ReceiverName || 'N/A'}
                                            </td>
                                            {showMyEatsPickupDeliveryAgentColumn && (
                                                <td>
                                                    {assignedAgents[orderId] ? (
                                                        <span className="assigned-agent-name">
                                                            {assignedAgents[orderId]}
                                                        </span>
                                                    ) : !isOrderHistory && !isCancelledOrder ? (
                                                        <button
                                                            type="button"
                                                            className="assign-agent-link"
                                                            onClick={() =>
                                                                handleOpenStatusUpdateModal(
                                                                    orderId,
                                                                    order.orderStatus ?? order.OrderStatus
                                                                )
                                                            }
                                                        >
                                                            Assign
                                                        </button>
                                                    ) : (
                                                        isOrderHistory ? 'Not assigned' : '—'
                                                    )}
                                                </td>
                                            )}
                                        </>
                                    )}
                                    {!isOrderHistory && (
                                        <td className="action-cell menu-table-actions-cell">
                                            {/* One-tap advance to the next step (no dropdown reading). */}
                                            {onlineNextStep && !isCancelledOrder && (
                                                <button
                                                    type="button"
                                                    className="order-next-step-btn"
                                                    title={onlineNextStep.actionLabel}
                                                    onClick={() => {
                                                        // MyEats: Start cooking is one-tap; Mark ready opens
                                                        // the update modal for delivery time / agent.
                                                        const openUpdateModalForNextStep =
                                                            (isMyEatsOrdersView(orderTypeFilter) &&
                                                                onlineNextStep.nextKey === 'READY') ||
                                                            (orderTypeFilter === 'jreats' &&
                                                                onlineNextStep.nextKey === 'OFD');
                                                        if (openUpdateModalForNextStep) {
                                                            void handleOpenStatusUpdateModal(
                                                                orderId,
                                                                order.orderStatus ?? order.OrderStatus,
                                                                onlineNextStep.nextStatus
                                                            );
                                                        } else {
                                                            handleStatusChange(
                                                                orderId,
                                                                onlineNextStep.nextStatus
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {onlineNextStep.actionLabel}
                                                </button>
                                            )}
                                            {isMyEatsOrdersView(orderTypeFilter) &&
                                                resolveStatusKey(
                                                    order.orderStatus ?? order.OrderStatus
                                                ) === 'PREPARING' &&
                                                !isCancelledOrder && (
                                                    <button
                                                        type="button"
                                                        className="action-button"
                                                        title="Edit delivery time / agent"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            void handleOpenStatusUpdateModal(
                                                                orderId,
                                                                order.orderStatus ?? order.OrderStatus
                                                            );
                                                        }}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                )}
                                            {/* Active orders: hide actions for delivered or cancelled. */}
                                            {isDeliveredOnly || isCancelledOrder ? null : (
                                                <button
                                                    type="button"
                                                    className="order-cancel-btn"
                                                    title="Cancel order"
                                                    onClick={() =>
                                                        handleActionClick(orderId, 'cancel')
                                                    }
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                                {selectedOrderItems === orderId && (
                                    <tr>
                                        <td colSpan={onlineOrdersTableColSpan(
                                            onlineOrderType,
                                            orderTypeFilter,
                                            effectiveOnlineOrderTypeFilter
                                        )}>
                                            <ul className="today-order-food-list">
                                                    {lineItems.map((item, idx) => (
                                                        <li key={item.orderLineId ?? idx}>
                                                            {item.foodItemName ?? item.FoodItemName} - Quantity: {item.foodQuantity ?? item.FoodQuantity}
                                                        , Price: {item.price ?? item.Price}
                                                        </li>
                                                ))}
                                            </ul>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                            );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan={onlineOrdersTableColSpan(
                                        onlineOrderType,
                                        orderTypeFilter,
                                        effectiveOnlineOrderTypeFilter
                                    )}
                                    className="no-orders-message menu-table-empty-cell"
                                >
                                    {orderTypeFilter === 'jreats'
                                        ? onlineOrderType === 'orderHistory'
                                            ? 'No order history'
                                            : 'No Junior Eats orders'
                                        : 'No orders found'}
                                </td>
                            </tr>
                        )}
                                </tbody>
                            </table>
                        </OrdersTablePanel>
                            <OrdersTablePagination
                                currentPage={currentPageOnline}
                                totalPages={totalPagesOnline}
                                totalItems={filteredOnlineOrders.length}
                                pageSize={ordersPageSize}
                                onPageChange={handlePageChangeOnline}
                                onPageSizeChange={handleOrdersPageSizeChange}
                            />
                        </>
                    ) : (
                        <div style={{ 
                            padding: '40px', 
                            textAlign: 'center', 
                            color: '#666',
                            fontSize: '16px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '4px',
                            marginTop: '20px'
                        }}>
                            Loading restaurant orders…
                        </div>
                    )}
                </>
            )}
    
            {isAssignAgentsPopupVisible && (
                <div className="popup-overlay">
                    <div className="popup-box popup-box--assign-agents">
                        <div className="assign-agents-modal-header">
                            <h2>Assign Agents</h2>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                aria-label="Close"
                                onClick={handleCloseAssignAgentsPopup}
                                disabled={assignAgentsSaving}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="assign-agents-form">
                            <label
                                htmlFor="assign-agents-school"
                                className="today-order-modal-field-label"
                            >
                                Select school
                            </label>
                            <SearchableSelect
                                id="assign-agents-school"
                                value={assignAgentsSchool}
                                onChange={setAssignAgentsSchool}
                                options={assignAgentsSchoolSelectOptions}
                                placeholder="— Select school —"
                                searchPlaceholder="Search schools"
                                disabled={assignAgentsSaving}
                                className="assign-agents-form__field"
                                triggerClassName="assign-agents-form__trigger"
                                dropdownMinWidth={280}
                                emptyMessage="No schools found"
                            />

                            <label
                                htmlFor="assign-agents-delivery-agent"
                                className="today-order-modal-field-label assign-agents-form__agent-label"
                            >
                                Assign delivery agent
                            </label>
                            {assignAgentsSaving ? (
                                <div
                                    className="today-order-modal-head-secondary"
                                    aria-live="polite"
                                >
                                    Saving…
                                </div>
                            ) : null}
                            {deliveryAgents.length === 0 ? (
                                <p className="today-order-modal-no-agents">
                                    No delivery agents available.
                                </p>
                            ) : (
                                <SearchableSelect
                                    id="assign-agents-delivery-agent"
                                    value={assignAgentsSelectedId}
                                    onChange={setAssignAgentsSelectedId}
                                    options={assignAgentsDeliverySelectOptions}
                                    placeholder="— Select delivery agent —"
                                    searchPlaceholder="Search delivery agents"
                                    disabled={assignAgentsSaving}
                                    className="assign-agents-form__field"
                                    triggerClassName="assign-agents-form__trigger"
                                    dropdownMinWidth={280}
                                    emptyMessage="No agents found"
                                />
                            )}

                            <div className="assign-agents-form__actions">
                                <button
                                    type="button"
                                    className="add-button"
                                    onClick={handleAssignAgentsSubmit}
                                    disabled={assignAgentsSaving}
                                >
                                    {assignAgentsSaving ? 'Assigning…' : 'Assign'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isPopupVisible && (
                <div className="popup-overlay">
                    <div className="popup-box popup-box--update-order">
                        <>
                                <div className="assign-agents-modal-header">
                                    <h2>Update order</h2>
                                    <button
                                        type="button"
                                        className="menu-template-modal-close"
                                        aria-label="Close"
                                        onClick={handleClosePopup}
                                        disabled={deliveryAssignSaving}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                                {isOnlineOrdersView(orderTypeFilter) ? (
                                    <>
                                        <div
                                            className={`today-order-modal-online-fields${
                                                showDeliveryAgentInModal
                                                    ? ''
                                                    : ' today-order-modal-online-fields--status-time-row'
                                            }`}
                                        >
                                            <div className="today-order-modal-status-column">
                                                <label
                                                    htmlFor="status-update-select"
                                                    className="today-order-modal-field-label"
                                                >
                                                    Status
                                                </label>
                                                {deliveryAssignSaving ? (
                                                    <div
                                                        className="today-order-modal-head-secondary"
                                                        aria-live="polite"
                                                    >
                                                        Saving…
                                                    </div>
                                                ) : null}
                                                <select
                                                    id="status-update-select"
                                                    className="today-order-select"
                                                    value={selectedStatusForUpdate}
                                                    onChange={(e) => setSelectedStatusForUpdate(e.target.value)}
                                                    style={{ width: '100%' }}
                                                >
                                                    {modalStatusUpdateOptions.map((status) => (
                                                        <option key={status} value={status}>
                                                            {orderTypeFilter === 'jreats'
                                                                ? jreatsStatusOptionLabel(status)
                                                                : status}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {showDeliveryAgentInModal && (
                                            <div className="today-order-modal-agent-column">
                                                <div className="today-order-modal-field-head">
                                                    <div
                                                        id="delivery-agent-status-modal-label"
                                                        className="today-order-modal-field-label"
                                                    >
                                                        Assign delivery agent
                                                    </div>
                                                    {deliveryAssignSaving ? (
                                                        <div
                                                            className="today-order-modal-head-secondary"
                                                            aria-live="polite"
                                                        >
                                                            Saving…
                                                        </div>
                                                    ) : null}
                                                </div>
                                                {deliveryAgents.length === 0 ? (
                                                    <p className="today-order-modal-no-agents">
                                                        No delivery agents available.
                                                    </p>
                                                ) : (
                                                    <details
                                                        ref={deliveryAgentDropdownRef}
                                                        className={`today-order-delivery-agent-dropdown${
                                                            deliveryAssignSaving
                                                                ? ' today-order-delivery-agent-dropdown--busy'
                                                                : ''
                                                        }`}
                                                        aria-busy={deliveryAssignSaving}
                                                        onToggle={(e) => {
                                                            if (!e.currentTarget.open) setDeliveryAgentSearch('');
                                                        }}
                                                    >
                                                        <summary
                                                            className="today-order-delivery-agent-summary"
                                                            aria-labelledby="delivery-agent-status-modal-label"
                                                        >
                                                            {modalDeliveryAgentSummaryLabel}
                                                        </summary>
                                                        <div className="today-order-delivery-agent-panel">
                                                            <div className="today-order-delivery-agent-search-wrap">
                                                                <input
                                                                    type="text"
                                                                    className="today-order-delivery-agent-search-input"
                                                                    placeholder="Search delivery agents"
                                                                    value={deliveryAgentSearch}
                                                                    onChange={(e) =>
                                                                        setDeliveryAgentSearch(e.target.value)
                                                                    }
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                                {deliveryAgentSearch.trim() !== '' && (
                                                                    <button
                                                                        type="button"
                                                                        className="today-order-delivery-agent-search-clear"
                                                                        aria-label="Clear delivery agent search"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setDeliveryAgentSearch('');
                                                                        }}
                                                                    >
                                                                        <FaTimes aria-hidden />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="today-order-delivery-agent-options">
                                                                {filteredDeliveryAgentsForModal.length === 0 ? (
                                                                    <div className="today-order-delivery-agent-empty">
                                                                        No agents found
                                                                    </div>
                                                                ) : (
                                                                    filteredDeliveryAgentsForModal.map((agent) => {
                                                                        const rid = getDeliveryAgentRecordId(agent);
                                                                        const phone = getDeliveryAgentPhone(agent);
                                                                        return (
                                                                            <button
                                                                                key={rid}
                                                                                type="button"
                                                                                className="today-order-delivery-agent-option"
                                                                                onClick={() =>
                                                                                    handleDeliveryAgentModalPick(rid)
                                                                                }
                                                                            >
                                                                                {getDeliveryAgentDisplayName(agent)}
                                                                                {phone ? ` — ${formatMobileDisplayOrFallback(phone, '')}` : ''}
                                                                            </button>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                            )}
                                            {orderTypeFilter !== 'jreats' ? (
                                            <div className="today-order-modal-delivery-time-block">
                                                <label
                                                    htmlFor="delivery-time-update-modal"
                                                    className="today-order-modal-field-label"
                                                >
                                                    Delivery time
                                                </label>
                                                <input
                                                    id="delivery-time-update-modal"
                                                    type="datetime-local"
                                                    className="today-order-select"
                                                    value={deliveryTimeModalLocal}
                                                    onChange={(e) =>
                                                        setDeliveryTimeModalLocal(e.target.value)
                                                    }
                                                    style={{
                                                        width: '100%',
                                                        margin: 0,
                                                        boxSizing: 'border-box',
                                                        padding: '8px 10px',
                                                        fontSize: '14px',
                                                        border: '1px solid #c5ccd6',
                                                        borderRadius: '4px',
                                                    }}
                                                    aria-label="Delivery time"
                                                />
                                            </div>
                                            ) : (
                                            <div className="today-order-modal-delivery-time-block">
                                                <div className="today-order-modal-field-label">
                                                    Delivery time
                                                </div>
                                                <div
                                                    className="today-order-modal-delivery-time-readonly"
                                                    aria-live="polite"
                                                >
                                                    {formatJreatsMealDeliveryDisplay(
                                                        modalOrderRow,
                                                        jreatsMealDeliveryTimes ?? {}
                                                    )}
                                                </div>
                                            </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className="today-order-modal-status-column"
                                        style={{ marginTop: '16px' }}
                                    >
                                        <label
                                            htmlFor="status-update-select"
                                            className="today-order-modal-field-label"
                                        >
                                            Status
                                        </label>
                                        <select
                                            id="status-update-select"
                                            className="today-order-select"
                                            value={selectedStatusForUpdate}
                                            onChange={(e) => setSelectedStatusForUpdate(e.target.value)}
                                            style={{ width: '100%' }}
                                        >
                                            {(orderTypeFilter === 'offline'
                                                ? dineInStatusUpdateOptions
                                                : orderTypeFilter === 'catering'
                                                  ? cateringStatusUpdateOptions
                                                  : onlineStatusUpdateOptions
                                            ).map((status) => (
                                                <option key={status} value={status}>
                                                    {status}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="today-order-update-form__actions">
                                    <button type="button" className="add-button" onClick={handleSubmitStatusUpdate}>
                                        Update
                                    </button>
                                </div>
                            </>
                    </div>
                </div>
            )}
        </div>
    );
};    
export default TodayOrder;
